import Info from "@assets/icons/info.svg";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import DataPill from "@components/pills/DataPill";
import { CountriesDropdown } from "@components/dropdowns/CountriesDropdown";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import LabelRow from "@components/rowcomponents/LabelRow";
import SlidingPanel from "@components/sliders/SlidingPanel";
import HorizontalStepper from "@components/stepper/HorizontalStepper";
import { commonApiServices } from "@services/apis/Common";
import { invoiceServices } from "@services/apis/Invoice";
import { GetCountriesListModel } from "@services/models/Common/GetCountriesList";
import { GetExchangesData } from "@services/models/InvoiceApprovals/GetExchangesModel";
import {
  AdminNote,
  RequestsInvoiceData,
} from "@services/models/InvoiceApprovals/GetInvoiceApprovalsModel";
import React, { useEffect, useState } from "react";
import DocumentViewerModal from "src/modules/InvoiceApprovals/components/DocumentViewerModal";
import EnhancedNotesSection from "src/modules/InvoiceApprovals/components/EnhancedNotesSection";
import webSocketService from "../../services/websocket/WebsocketServices";
import ConstantFunctions from "../../utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import BankAccountSelectionComponent from "./components/BankAccountSelectionComponent";
import { StatusPillRenderer } from "./components/StatusPillRenderer";
import UploadFileComponent from "./components/UploadFileComponent";

interface InvoiceApprovalsDetailsProps {
  data: RequestsInvoiceData;
  onDataUpdate?: () => void;
}

const InvoiceApprovalsDetails: React.FC<InvoiceApprovalsDetailsProps> = ({
  data: initialData,
  onDataUpdate,
}) => {
  const [data, setData] = useState(initialData);
  const [actionLoading, setActionLoading] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [transactionLoading, setTransactionLoading] = useState<boolean>(false);
  const constantFunctions = new ConstantFunctions();
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [bankAccountDetailsVisible, setBankAccountDetailsVisible] =
    useState<boolean>(false);

  const [countries, setCountries] = useState<GetCountriesListModel["data"]>([]);
  const [countriesLoading, setCountriesLoading] = useState<boolean>(false);
  const [exchanges, setExchanges] = useState<GetExchangesData[]>([]);
  const [exchangesLoading, setExchangesLoading] = useState<boolean>(false);

  // Document viewer modal state
  const [documentViewerOpen, setDocumentViewerOpen] = useState<boolean>(false);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState<string>("");
  const [selectedDocumentTitle, setSelectedDocumentTitle] =
    useState<string>("");
  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState<boolean>(false);

  const [transactionData, setTransactionData] = useState({
    selectedCurrency: data.transactionData?.currencySymbol || "",
    selectedNetwork: data.transactionData?.networkSymbol || "",
    step: data.transactionData?.step || 1,
    cryptoToFiatList: data.transactionData?.cryptoToFiatList || [],
    offRampTransfer: data.transactionData?.offrampTransfer || undefined,
    withdrawFiatToTyltBankAccount: data.transactionData
      ?.withdrawFiatToTyltBankAccount || {
      withdrawFiatToTyltBankAccountReferenceNumber: "",
      withdrawFiatToTyltBankAccountExchange: "",
      toBankAccount: {
        bankId: 0,
        bankName: "",
        accountName: "",
        accountNo: "",
        amountSent: 0,
        amountReceived: 0,
      },
    },
    moveFiatFromTyltToClientPoolAccount: data.transactionData
      ?.moveFiatFromTyltToClientPoolAccount || {
      moveFiatFromTyltToClientPoolAccountReferenceNumber: "",
      moveFiatFromTyltToClientPoolAccountExchange: "",
      fromBankAccount: {
        bankId: 0,
        bankName: "",
        accountName: "",
        accountNo: "",
        amountSent: 0,
        amountReceived: 0,
      },
      toBankAccount: {
        bankId: 0,
        bankName: "",
        accountName: "",
        accountNo: "",
        amountSent: 0,
        amountReceived: 0,
      },
    },
    remitFiatToMerchantBankAccount: data.transactionData
      ?.remitFiatToMerchantBankAccount || {
      remitFiatToMerchantBankAccountReferenceNumber: "",
      fromBankAccount: {
        bankId: 0,
        bankName: "",
        accountName: "",
        accountNo: "",
        amountSent: 0,
        amountReceived: 0,
      },
      toMerchantBankAccount: {
        bankId: 0,
        bankName: "",
        accountName: "",
        accountNo: "",
        amountSent: 0,
        amountReceived: 0,
      },
    },
  });

  const [newTransaction, setNewTransaction] = useState({
    refrenceNumber: "",
    transactionType: "",
  });

  const [withdrawFiatToTyltBankAccount, setWithdrawFiatToTyltBankAccount] =
    useState({
      withdrawFiatToTyltBankAccountReferenceNumber: "",
      withdrawFiatToTyltBankAccountExchange: "",
    });

  // Bank selection state
  const [
    withdrawFiatToTyltBankAccountselectedBankData,
    setwithdrawFiatToTyltBankAccountSelectedBankData,
  ] = useState<{
    bankId: number;
    bankName: string;
    accountName: string;
    accountNo: string;
    amountSent: number;
    amountReceived: number;
  } | null>(null);

  const [
    moveFiatFromTyltToClientPoolAccountRefrenceNumber,
    setMoveFiatFromTyltToClientPoolAccountRefrenceNumber,
  ] = useState<string>("");
  const [
    moveFiatFromTyltToClientPoolAccountselectedBankDataTo,
    setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo,
  ] = useState<{
    bankId: number;
    bankName: string;
    accountName: string;
    accountNo: string;
    amountSent: number;
    amountReceived: number;
  } | null>(null);
  const [
    moveFiatFromTyltToClientPoolAccountselectedBankDataFrom,
    setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom,
  ] = useState<{
    bankId: number;
    bankName: string;
    accountName: string;
    accountNo: string;
    amountSent: number;
    amountReceived: number;
  } | null>(null);

  // Step 7: Fiat remittance to merchant bank account state
  const [
    remitFiatToMerchantBankAccountReferenceNumber,
    setRemitFiatToMerchantBankAccountReferenceNumber,
  ] = useState<string>("");
  const [
    remitFiatToMerchantBankAccountSelectedBankDataFrom,
    setRemitFiatToMerchantBankAccountSelectedBankDataFrom,
  ] = useState<{
    bankId: number;
    bankName: string;
    accountName: string;
    accountNo: string;
    amountSent: number;
    amountReceived: number;
  } | null>(null);

  const [enhancedNotes, setEnhancedNotes] = useState<AdminNote[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(
    data.transactionData?.step || 1,
  );

  // Edit/Delete states for cryptoToFiatList
  const [isEditSliderOpen, setIsEditSliderOpen] = useState<boolean>(false);
  const [editingTransactionIndex, setEditingTransactionIndex] = useState<
    number | null
  >(null);
  const [editTransactionData, setEditTransactionData] = useState({
    refrenceNumber: "",
    transactionType: "",
  });

  const [editData, setEditData] = useState({
    // =====================
    // Invoice basics
    // =====================
    invoiceName: data.requestData?.invoiceName || "",
    baseAmount: data.requestData?.baseAmount || 0,
    baseCurrencyId: data.requestData?.baseCurrencyId || undefined,
    bankMethodId: data.requestData?.bankMethodId || undefined,

    settledCurrenciesList: data.requestData?.settledCurrenciesList || [],
    settledNetworksList: data.requestData?.settledNetworksList || [],

    // =====================
    // Buyer KYC – Personal
    // =====================
    clientName: data.requestData?.clientName || "",
    dateOfBirth: data.requestData?.dateOfBirth || "",
    email: data.requestData?.email || "",
    mobileNumber: data.requestData?.mobileNumber || "",
    politicallyExposed: data.requestData?.politicallyExposed ?? undefined,

    // =====================
    // Buyer KYC – Nationality
    // =====================
    countryOfResidence: data.requestData?.countryOfResidence || "",
    countryOfResidenceId: data.requestData?.countryOfResidenceId || undefined,

    countryOfNationality: data.requestData?.countryOfNationality || "",
    countryOfNationalityId:
      data.requestData?.countryOfNationalityId || undefined,

    countryOfBirth: data.requestData?.countryOfBirth || "",
    countryOfBirthId: data.requestData?.countryOfBirthId || undefined,

    dualNationality: data.requestData?.dualNationality || "",
    dualNationalityId: data.requestData?.dualNationalityId || undefined,

    // =====================
    // Buyer KYC – Employment
    // =====================
    occupation: data.requestData?.occupation || "",
    companyName: data.requestData?.companyName || "",
    natureOfBusiness: data.requestData?.natureOfBusiness || "",

    // =====================
    // Property Details
    // =====================
    projectName: data.requestData?.projectName || "",
    unitNumber: data.requestData?.unitNumber || "",
    unitPurchasePrice: data.requestData?.unitPurchasePrice || 0,

    // =====================
    // AML
    // =====================
    sourceOfFunds: data.requestData?.sourceOfFunds || "",
    sourceOfWealth: data.requestData?.sourceOfWealth || "",

    // =====================
    // Documents
    // =====================
    governmentIdUrl: data.requestData?.governmentIdUrl || "",
    proofOfAddressUrl: data.requestData?.proofOfAddressUrl || "",
    sowDocumentsUrl: data.requestData?.sowDocumentsUrl || "",
    sofDocumentsUrl: data.requestData?.sofDocumentsUrl || "",

    purchaseAgreementUrl: data.requestData?.purchaseAgreementUrl || "",
    proofOfPropertyUrl: data.requestData?.proofOfPropertyUrl || "",
    invoicePaymentInstructionUrl:
      data.requestData?.invoicePaymentInstructionUrl || "",
  });

  const sowOptions = [
    { id: 1, name: "Salary Income" },
    { id: 2, name: "Business Income" },
    { id: 3, name: "Investment Income" },
    { id: 4, name: "Asset Sale" },
    { id: 5, name: "Inheritance" },
    { id: 6, name: "Other" },
  ];

  const sofOptions = [
    { id: 1, name: "Personal Wallet" },
    { id: 2, name: "Exchange Account" },
    { id: 3, name: "Corporate Wallet" },
    { id: 4, name: "Mining" },
    { id: 5, name: "Other" },
  ];
  const transactionTypesOptions = [
    { id: 1, name: "Crypto to Crypto" },
    { id: 2, name: "Fiat to Crypto" },
    { id: 3, name: "Crypto to Fiat" },
  ];

  const handleApprove = async () => {
    try {
      setActionLoading("approve");
      await invoiceServices.approveInvoice({
        requestId: data.requestId,
      });

      await handleUpdateTransactionData(false, 2);

      handleSuccess({
        message: "Invoice approved successfully",
      });

      // Call the parent callback to refresh the list
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Failed to approve invoice. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setActionLoading("");
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading("reject");
      await invoiceServices.rejectInvoice({
        requestId: data.requestId,
      });
      handleSuccess({
        message: "Invoice rejected successfully",
      });

      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Failed to reject invoice. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setActionLoading("");
    }
  };

  const openDocument = (url: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const openDocumentInModal = (url: string, title: string) => {
    if (url) {
      setSelectedDocumentUrl(url);
      setSelectedDocumentTitle(title);
      setDocumentViewerOpen(true);
    }
  };

  const closeDocumentViewer = () => {
    setDocumentViewerOpen(false);
    setSelectedDocumentUrl("");
    setSelectedDocumentTitle("");
  };

  const fetchCountries = async () => {
    try {
      setCountriesLoading(true);
      const response = await commonApiServices.getCountriesList();
      setCountries(response.data);
    } catch (error) {
      handleError({ message: "Failed to load countries" });
    } finally {
      setCountriesLoading(false);
    }
  };

  const [selectedExchange, setSelectedExchange] = useState<number | undefined>(
    data.transactionData?.exchangeId || undefined,
  );

  // New state variables for step 3 exchange and network selection
  const [selectedExchangeName, setSelectedExchangeName] = useState<string>("");
  const [selectedNetworkName, setSelectedNetworkName] = useState<string>("");
  const [availableNetworks, setAvailableNetworks] = useState<string[]>([]);

  const fetchExchanges = async () => {
    try {
      setExchangesLoading(true);
      const response = await invoiceServices.getExchanges();
      setExchanges(response.data);

      if (!selectedExchange && response.data.length > 0) {
        setSelectedExchange(response.data[0]?.exchangeId);
      }
    } catch (error) {
    } finally {
      setExchangesLoading(false);
    }
  };

  const handleEditToggle = async () => {
    if (!isEditing) {
      try {
        await invoiceServices.markInvoiceRequestIncomplete({
          requestId: data.requestId,
        });
        setData((prevData) => ({
          ...prevData,
          status: "INCOMPLETE_INFORMATION",
        }));
        handleSuccess({
          message: "Invoice marked as incomplete for additional information",
        });

        if (onDataUpdate) {
          onDataUpdate();
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.msg || "Failed to mark invoice as incomplete";
        handleError({ message: errorMessage });
        return;
      }
    }

    setIsEditing(!isEditing);
    if (!isEditing && countries.length === 0) {
      fetchCountries();
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCountrySelection = (field: string, value: string | number) => {
    const selectedId = Number(value);
    const country = countries.find((c) => c.id === selectedId);

    if (field === "countryOfBirth") {
      setEditData((prev) => ({
        ...prev,
        countryOfBirthId: selectedId,
        countryOfBirth: country?.country || "",
      }));
    } else if (field === "countryOfResidence") {
      setEditData((prev) => ({
        ...prev,
        countryOfResidenceId: selectedId,
        countryOfResidence: country?.country || "",
      }));
    } else if (field === "countryOfNationality") {
      setEditData((prev) => ({
        ...prev,
        countryOfNationalityId: selectedId,
        countryOfNationality: country?.country || "",
      }));
    }
  };

  const handleDropdownSelection = (
    field: string,
    options: any[],
    value: string | number,
  ) => {
    const selectedId = Number(value);
    const option = options.find((opt) => opt.id === selectedId);
    setEditData((prev) => ({
      ...prev,
      [field]: option?.name || "",
    }));
  };

  const handleUpdateInvoiceDetails = async () => {
    try {
      setUpdateLoading(true);

      const requestData = {
        baseAmount: editData.baseAmount,
        sourceOfFunds: editData.sourceOfFunds,
        sourceOfWealth: editData.sourceOfWealth,
        countryOfBirth: editData.countryOfBirth,
        countryOfResidence: editData.countryOfResidence,
        countryOfNationality: editData.countryOfNationality,
        countryOfBirthId: editData.countryOfBirthId,
        countryOfResidenceId: editData.countryOfResidenceId,
        countryOfNationalityId: editData.countryOfNationalityId,
        governmentIdUrl: editData.governmentIdUrl,
        proofOfAddressUrl: editData.proofOfAddressUrl,
        sowDocumentsUrl: editData.sowDocumentsUrl,
        sofDocumentsUrl: editData.sofDocumentsUrl,
        purchaseAgreementUrl: editData.purchaseAgreementUrl,
        proofOfPropertyUrl: editData.proofOfPropertyUrl,
        invoicePaymentInstructionUrl: editData.invoicePaymentInstructionUrl,
        projectName: editData.projectName,
        unitNumber: editData.unitNumber,
        unitPurchasePrice: Number(editData.unitPurchasePrice),
      };

      await invoiceServices.updateInvoiceRequestData({
        requestId: data.requestId,
        requestData,
      });

      setData((prevData) => ({
        ...prevData,
        requestData: {
          ...prevData.requestData,
          baseAmount: editData.baseAmount,
          sourceOfFunds: editData.sourceOfFunds,
          sourceOfWealth: editData.sourceOfWealth,
          countryOfBirth: editData.countryOfBirth,
          countryOfResidence: editData.countryOfResidence,
          countryOfNationality: editData.countryOfNationality,
          countryOfBirthId: editData.countryOfBirthId || 0,
          countryOfResidenceId: editData.countryOfResidenceId || 0,
          countryOfNationalityId: editData.countryOfNationalityId || 0,
          governmentIdUrl: editData.governmentIdUrl,
          proofOfAddressUrl: editData.proofOfAddressUrl,
          sowDocumentsUrl: editData.sowDocumentsUrl,
          sofDocumentsUrl: editData.sofDocumentsUrl,
          purchaseAgreementUrl: editData.purchaseAgreementUrl,
          proofOfPropertyUrl: editData.proofOfPropertyUrl,
          invoicePaymentInstructionUrl: editData.invoicePaymentInstructionUrl,
          projectName: editData.projectName,
          unitNumber: editData.unitNumber,
          unitPurchasePrice: editData.unitPurchasePrice,
        },
      }));

      handleSuccess({ message: "Invoice details updated successfully" });
      setIsEditing(false);

      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to update invoice details";
      handleError({ message: errorMessage });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdateTransactionData = async (
    isStep3 = false,
    stepNumber?: number,
  ) => {
    try {
      setTransactionLoading(true);

      let updatedCryptoToFiatList = transactionData.cryptoToFiatList;
      if (newTransaction.refrenceNumber && newTransaction.transactionType) {
        updatedCryptoToFiatList = [
          ...transactionData.cryptoToFiatList,
          {
            refrenceNumber: newTransaction.refrenceNumber,
            transactionType: newTransaction.transactionType,
          },
        ];
      }

      const transactionDataPayload: any = {
        exchangeId: selectedExchange!,
        currencySymbol: transactionData.selectedCurrency,
        networkSymbol: transactionData.selectedNetwork,
        cryptoToFiatList: updatedCryptoToFiatList,
        ...(stepNumber &&
          transactionData.step < stepNumber && { step: stepNumber }),
      };

      // Add withdrawFiatToTyltBankAccount if data exists
      if (
        withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountReferenceNumber ||
        withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountExchange ||
        withdrawFiatToTyltBankAccountselectedBankData
      ) {
        transactionDataPayload.withdrawFiatToTyltBankAccount = {
          withdrawFiatToTyltBankAccountReferenceNumber:
            withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountReferenceNumber,
          withdrawFiatToTyltBankAccountExchange:
            withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountExchange,
          ...(withdrawFiatToTyltBankAccountselectedBankData && {
            toBankAccount: {
              bankId: withdrawFiatToTyltBankAccountselectedBankData.bankId,
              bankName: withdrawFiatToTyltBankAccountselectedBankData.bankName,
              accountName:
                withdrawFiatToTyltBankAccountselectedBankData.accountName,
              accountNo:
                withdrawFiatToTyltBankAccountselectedBankData.accountNo,
              amountSent:
                withdrawFiatToTyltBankAccountselectedBankData.amountSent,
              amountReceived:
                withdrawFiatToTyltBankAccountselectedBankData.amountReceived,
            },
          }),
        };
      }

      // Add moveFiatFromTyltToClientPoolAccount if data exists (Step 6)
      if (
        moveFiatFromTyltToClientPoolAccountRefrenceNumber ||
        moveFiatFromTyltToClientPoolAccountselectedBankDataFrom ||
        moveFiatFromTyltToClientPoolAccountselectedBankDataTo
      ) {
        transactionDataPayload.moveFiatFromTyltToClientPoolAccount = {
          moveFiatFromTyltToClientPoolAccountReferenceNumber:
            moveFiatFromTyltToClientPoolAccountRefrenceNumber,
          ...(moveFiatFromTyltToClientPoolAccountselectedBankDataFrom && {
            fromBankAccount: {
              bankId:
                moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.bankId,
              bankName:
                moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.bankName,
              accountName:
                moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.accountName,
              accountNo:
                moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.accountNo,
              amountSent:
                moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.amountSent,
              amountReceived:
                moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.amountReceived,
            },
          }),
          ...(moveFiatFromTyltToClientPoolAccountselectedBankDataTo && {
            toBankAccount: {
              bankId:
                moveFiatFromTyltToClientPoolAccountselectedBankDataTo.bankId,
              bankName:
                moveFiatFromTyltToClientPoolAccountselectedBankDataTo.bankName,
              accountName:
                moveFiatFromTyltToClientPoolAccountselectedBankDataTo.accountName,
              accountNo:
                moveFiatFromTyltToClientPoolAccountselectedBankDataTo.accountNo,
              amountSent:
                moveFiatFromTyltToClientPoolAccountselectedBankDataTo.amountSent,
              amountReceived:
                moveFiatFromTyltToClientPoolAccountselectedBankDataTo.amountReceived,
            },
          }),
        };
      }

      // Add remitFiatToMerchantBankAccount if data exists (Step 7)
      if (
        remitFiatToMerchantBankAccountReferenceNumber ||
        remitFiatToMerchantBankAccountSelectedBankDataFrom
      ) {
        transactionDataPayload.remitFiatToMerchantBankAccount = {
          remitFiatToMerchantBankAccountReferenceNumber:
            remitFiatToMerchantBankAccountReferenceNumber,
          ...(remitFiatToMerchantBankAccountSelectedBankDataFrom && {
            fromBankAccount: {
              bankId: remitFiatToMerchantBankAccountSelectedBankDataFrom.bankId,
              bankName:
                remitFiatToMerchantBankAccountSelectedBankDataFrom.bankName,
              accountName:
                remitFiatToMerchantBankAccountSelectedBankDataFrom.accountName,
              accountNo:
                remitFiatToMerchantBankAccountSelectedBankDataFrom.accountNo,
              amountSent:
                remitFiatToMerchantBankAccountSelectedBankDataFrom.amountSent,
              amountReceived:
                remitFiatToMerchantBankAccountSelectedBankDataFrom.amountReceived,
            },
          }),
        };
      }

      await invoiceServices.updateInvoiceTransactionData({
        requestId: data.requestId,
        transactionData: transactionDataPayload,
      });

      if (isStep3) {
        await invoiceServices.processOfframpInvoiceSettlement({
          invoiceId: Number(data.invoiceId),
        });
      }

      // Update local state with the complete transaction data
      setTransactionData((prev) => ({
        ...prev,
        cryptoToFiatList: updatedCryptoToFiatList,
        ...(transactionDataPayload.withdrawFiatToTyltBankAccount && {
          withdrawFiatToTyltBankAccount:
            transactionDataPayload.withdrawFiatToTyltBankAccount,
        }),
        ...(transactionDataPayload.moveFiatFromTyltToClientPoolAccount && {
          moveFiatFromTyltToClientPoolAccount:
            transactionDataPayload.moveFiatFromTyltToClientPoolAccount,
        }),
        ...(transactionDataPayload.remitFiatToMerchantBankAccount && {
          remitFiatToMerchantBankAccount:
            transactionDataPayload.remitFiatToMerchantBankAccount,
        }),
      }));

      // Update main data state to refresh display tables
      setData((prevData) => ({
        ...prevData,
        transactionData: {
          ...prevData.transactionData,
          cryptoToFiatList: updatedCryptoToFiatList,
          ...(transactionDataPayload.withdrawFiatToTyltBankAccount && {
            withdrawFiatToTyltBankAccount:
              transactionDataPayload.withdrawFiatToTyltBankAccount,
          }),
          ...(transactionDataPayload.moveFiatFromTyltToClientPoolAccount && {
            moveFiatFromTyltToClientPoolAccount:
              transactionDataPayload.moveFiatFromTyltToClientPoolAccount,
          }),
          ...(transactionDataPayload.remitFiatToMerchantBankAccount && {
            remitFiatToMerchantBankAccount:
              transactionDataPayload.remitFiatToMerchantBankAccount,
          }),
        },
      }));

      // Reset new transaction form if we added one
      if (newTransaction.refrenceNumber && newTransaction.transactionType) {
        setNewTransaction({
          refrenceNumber: "",
          transactionType: "",
        });
      }

      // Determine success message based on what was updated
      let successMessage = "Transaction data updated successfully";
      if (
        moveFiatFromTyltToClientPoolAccountRefrenceNumber ||
        moveFiatFromTyltToClientPoolAccountselectedBankDataFrom ||
        moveFiatFromTyltToClientPoolAccountselectedBankDataTo
      ) {
        successMessage = "Fiat transfer data updated successfully";
      } else if (
        withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountReferenceNumber ||
        withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountExchange
      ) {
        successMessage = "Withdrawal data updated successfully";
      } else if (
        updatedCryptoToFiatList.length > transactionData.cryptoToFiatList.length
      ) {
        successMessage = "Transaction added and data updated successfully";
      }

      handleSuccess({
        message: successMessage,
      });

      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to update transaction data";
      handleError({ message: errorMessage });
    } finally {
      setTransactionLoading(false);
    }
  };
  // Helper functions for step 3 new logic
  const getUniqueExchanges = () => {
    const uniqueExchangeNames = [
      ...new Set(exchanges.map((ex) => ex.exchange)),
    ];
    return uniqueExchangeNames.map((exchangeName, index) => ({
      id: index + 1,
      name: exchangeName,
    }));
  };

  const getNetworksForExchange = (exchangeName: string) => {
    const exchangesWithName = exchanges.filter(
      (ex) => ex.exchange === exchangeName,
    );
    const uniqueNetworks = [
      ...new Set(exchangesWithName.map((ex) => ex.network)),
    ];
    return uniqueNetworks;
  };

  const findExchangeId = (
    exchangeName: string,
    networkName: string,
    currencySymbol: string,
  ) => {
    const matchingExchanges = exchanges.filter(
      (ex) =>
        ex.exchange === exchangeName &&
        ex.network === networkName &&
        ex.token === currencySymbol,
    );

    if (matchingExchanges.length === 0) {
      return null;
    }

    if (matchingExchanges.length > 1) {
      return "duplicate";
    }

    return matchingExchanges[0].exchangeId;
  };

  const handleStep3ExchangeSelection = (value: string | number) => {
    const selectedId = Number(value);
    const uniqueExchanges = getUniqueExchanges();
    const selectedExchangeName =
      uniqueExchanges.find((ex) => ex.id === selectedId)?.name || "";

    setSelectedExchangeName(selectedExchangeName);
    setSelectedNetworkName("");

    if (selectedExchangeName) {
      const networks = getNetworksForExchange(selectedExchangeName);
      setAvailableNetworks(networks);
    } else {
      setAvailableNetworks([]);
    }
  };

  const handleStep3NetworkSelection = (value: string | number) => {
    const selectedId = Number(value);
    const networkName = availableNetworks[selectedId - 1] || "";
    setSelectedNetworkName(networkName);

    // Update the selectedExchange based on the combination
    if (transactionData.offRampTransfer?.currency === undefined) {
      handleError({
        message: `Currency symbol is missing in transaction data. Cannot determine exchange.`,
      });
      return;
    }
    if (
      selectedExchangeName &&
      networkName &&
      transactionData.offRampTransfer?.currency
    ) {
      const exchangeId = findExchangeId(
        selectedExchangeName,
        networkName,
        transactionData.offRampTransfer?.currency,
      );

      if (exchangeId === "duplicate") {
        handleError({
          message: `Multiple exchanges found for ${selectedExchangeName} - ${networkName} - ${transactionData.offRampTransfer?.currency}. Please contact admin.`,
        });
        return;
      }

      if (exchangeId === null) {
        handleError({
          message: `No exchange found for ${selectedExchangeName} - ${networkName} - ${transactionData.offRampTransfer?.currency}`,
        });
        return;
      }

      setSelectedExchange(exchangeId);
    }
  };

  useEffect(() => {
    setData(initialData);
    setCurrentStep(initialData.transactionData?.step || 1);
  }, [initialData]);

  const handleInvoiceUpdateWebSocket = (socketData: RequestsInvoiceData) => {
    if (socketData.requestId === data.requestId) {
      setData(socketData);
      setCurrentStep(socketData.transactionData?.step || 1);
      setTransactionData({
        selectedCurrency: socketData.transactionData?.currencySymbol || "",
        selectedNetwork: socketData.transactionData?.networkSymbol || "",
        step: socketData.transactionData?.step || 1,
        cryptoToFiatList: socketData.transactionData?.cryptoToFiatList || [],
        offRampTransfer:
          socketData.transactionData?.offrampTransfer || undefined,
        withdrawFiatToTyltBankAccount: socketData.transactionData
          ?.withdrawFiatToTyltBankAccount || {
          withdrawFiatToTyltBankAccountReferenceNumber: "",
          withdrawFiatToTyltBankAccountExchange: "",
          toBankAccount: {
            bankId: 0,
            bankName: "",
            accountName: "",
            accountNo: "",
            amountSent: 0,
            amountReceived: 0,
          },
        },
        moveFiatFromTyltToClientPoolAccount: socketData.transactionData
          ?.moveFiatFromTyltToClientPoolAccount || {
          moveFiatFromTyltToClientPoolAccountReferenceNumber: "",
          moveFiatFromTyltToClientPoolAccountExchange: "",
          fromBankAccount: {
            bankId: 0,
            bankName: "",
            accountName: "",
            accountNo: "",
            amountSent: 0,
            amountReceived: 0,
          },
          toBankAccount: {
            bankId: 0,
            bankName: "",
            accountName: "",
            accountNo: "",
            amountSent: 0,
            amountReceived: 0,
          },
        },
        remitFiatToMerchantBankAccount: socketData.transactionData
          ?.remitFiatToMerchantBankAccount || {
          remitFiatToMerchantBankAccountReferenceNumber: "",
          fromBankAccount: {
            bankId: 0,
            bankName: "",
            accountName: "",
            accountNo: "",
            amountSent: 0,
            amountReceived: 0,
          },
          toMerchantBankAccount: {
            bankId: 0,
            bankName: "",
            accountName: "",
            accountNo: "",
            amountSent: 0,
            amountReceived: 0,
          },
        },
      });
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "admin_invoice_request_update",
          true,
          handleInvoiceUpdateWebSocket,
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected, data.requestId]);

  useEffect(() => {
    if (exchanges.length === 0) {
      fetchExchanges();
    }
  }, []);

  useEffect(() => {
    setEditData({
      // =====================
      // Invoice basics
      // =====================
      invoiceName: data.requestData?.invoiceName || "",
      baseAmount: data.requestData?.baseAmount || 0,
      baseCurrencyId: data.requestData?.baseCurrencyId || undefined,
      bankMethodId: data.requestData?.bankMethodId || undefined,

      settledCurrenciesList: data.requestData?.settledCurrenciesList || [],
      settledNetworksList: data.requestData?.settledNetworksList || [],

      // =====================
      // Buyer KYC – Personal
      // =====================
      clientName: data.requestData?.clientName || "",
      dateOfBirth: data.requestData?.dateOfBirth || "",
      email: data.requestData?.email || "",
      mobileNumber: data.requestData?.mobileNumber || "",
      politicallyExposed: data.requestData?.politicallyExposed ?? undefined,

      // =====================
      // Buyer KYC – Nationality
      // =====================
      countryOfBirth: data.requestData?.countryOfBirth || "",
      countryOfBirthId: data.requestData?.countryOfBirthId || undefined,

      countryOfResidence: data.requestData?.countryOfResidence || "",
      countryOfResidenceId: data.requestData?.countryOfResidenceId || undefined,

      countryOfNationality: data.requestData?.countryOfNationality || "",
      countryOfNationalityId:
        data.requestData?.countryOfNationalityId || undefined,

      dualNationality: data.requestData?.dualNationality || "",
      dualNationalityId: data.requestData?.dualNationalityId || undefined,

      // =====================
      // Buyer KYC – Employment
      // =====================
      occupation: data.requestData?.occupation || "",
      companyName: data.requestData?.companyName || "",
      natureOfBusiness: data.requestData?.natureOfBusiness || "",

      // =====================
      // Property Details
      // =====================
      projectName: data.requestData?.projectName || "",
      unitNumber: data.requestData?.unitNumber || "",
      unitPurchasePrice: data.requestData?.unitPurchasePrice || 0,

      // =====================
      // AML
      // =====================
      sourceOfFunds: data.requestData?.sourceOfFunds || "",
      sourceOfWealth: data.requestData?.sourceOfWealth || "",

      // =====================
      // Documents
      // =====================
      governmentIdUrl: data.requestData?.governmentIdUrl || "",
      proofOfAddressUrl: data.requestData?.proofOfAddressUrl || "",
      sowDocumentsUrl: data.requestData?.sowDocumentsUrl || "",
      sofDocumentsUrl: data.requestData?.sofDocumentsUrl || "",

      purchaseAgreementUrl: data.requestData?.purchaseAgreementUrl || "",
      proofOfPropertyUrl: data.requestData?.proofOfPropertyUrl || "",
      invoicePaymentInstructionUrl:
        data.requestData?.invoicePaymentInstructionUrl || "",
    });

    // Update transaction data when initial data changes
    setTransactionData({
      selectedCurrency: data.transactionData?.currencySymbol || "",
      selectedNetwork: data.transactionData?.networkSymbol || "",
      step: data.transactionData?.step || 1,
      offRampTransfer: data.transactionData?.offrampTransfer || undefined,
      cryptoToFiatList: data.transactionData?.cryptoToFiatList || [],
      withdrawFiatToTyltBankAccount: data.transactionData
        ?.withdrawFiatToTyltBankAccount || {
        withdrawFiatToTyltBankAccountReferenceNumber: "",
        withdrawFiatToTyltBankAccountExchange: "",
        toBankAccount: {
          bankId: 0,
          bankName: "",
          accountName: "",
          accountNo: "",
          amountSent: 0,
          amountReceived: 0,
        },
      },
      moveFiatFromTyltToClientPoolAccount: data.transactionData
        ?.moveFiatFromTyltToClientPoolAccount || {
        moveFiatFromTyltToClientPoolAccountReferenceNumber: "",
        moveFiatFromTyltToClientPoolAccountExchange: "",
        fromBankAccount: {
          bankId: 0,
          bankName: "",
          accountName: "",
          accountNo: "",
          amountSent: 0,
          amountReceived: 0,
        },
        toBankAccount: {
          bankId: 0,
          bankName: "",
          accountName: "",
          accountNo: "",
          amountSent: 0,
          amountReceived: 0,
        },
      },
      remitFiatToMerchantBankAccount: data.transactionData
        ?.remitFiatToMerchantBankAccount || {
        remitFiatToMerchantBankAccountReferenceNumber: "",
        fromBankAccount: {
          bankId: 0,
          bankName: "",
          accountName: "",
          accountNo: "",
          amountSent: 0,
          amountReceived: 0,
        },
        toMerchantBankAccount: {
          bankId: 0,
          bankName: "",
          accountName: "",
          accountNo: "",
          amountSent: 0,
          amountReceived: 0,
        },
      },
    });

    setEnhancedNotes(data.transactionData?.adminNotes || []);

    // Initialize bank data if exists, otherwise reset to null
    if (data.transactionData?.withdrawFiatToTyltBankAccount?.toBankAccount) {
      const bankAccount =
        data.transactionData.withdrawFiatToTyltBankAccount.toBankAccount;
      setwithdrawFiatToTyltBankAccountSelectedBankData({
        bankId: bankAccount.bankId,
        bankName: bankAccount.bankName,
        accountName: bankAccount.accountName,
        accountNo: bankAccount.accountNo,
        amountSent: bankAccount.amountSent || 0,
        amountReceived: bankAccount.amountReceived || 0,
      });
    } else {
      // Reset withdrawal bank data when no data exists
      setwithdrawFiatToTyltBankAccountSelectedBankData(null);
    }

    // Reset new transaction form
    setNewTransaction({
      refrenceNumber: "",
      transactionType: "",
    });

    // Update withdrawal data from initial data
    setWithdrawFiatToTyltBankAccount({
      withdrawFiatToTyltBankAccountReferenceNumber:
        data.transactionData?.withdrawFiatToTyltBankAccount
          ?.withdrawFiatToTyltBankAccountReferenceNumber || "",
      withdrawFiatToTyltBankAccountExchange:
        data.transactionData?.withdrawFiatToTyltBankAccount
          ?.withdrawFiatToTyltBankAccountExchange || "",
    });

    // Initialize step 6 data if exists, otherwise reset to defaults
    if (data.transactionData?.moveFiatFromTyltToClientPoolAccount) {
      const step6Data =
        data.transactionData.moveFiatFromTyltToClientPoolAccount;

      // Set reference number
      if (step6Data?.moveFiatFromTyltToClientPoolAccountReferenceNumber) {
        setMoveFiatFromTyltToClientPoolAccountRefrenceNumber(
          step6Data.moveFiatFromTyltToClientPoolAccountReferenceNumber,
        );
      } else {
        setMoveFiatFromTyltToClientPoolAccountRefrenceNumber("");
      }

      // Set from bank account data
      if (step6Data?.fromBankAccount) {
        setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom({
          bankId: step6Data.fromBankAccount.bankId,
          bankName: step6Data.fromBankAccount.bankName,
          accountName: step6Data.fromBankAccount.accountName,
          accountNo: step6Data.fromBankAccount.accountNo,
          amountSent: step6Data.fromBankAccount.amountSent || 0,
          amountReceived: step6Data.fromBankAccount.amountReceived || 0,
        });
      } else {
        setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom(null);
      }

      // Set to client pool account data
      if (step6Data?.toBankAccount) {
        setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo({
          bankId: step6Data.toBankAccount.bankId,
          bankName: step6Data.toBankAccount.bankName,
          accountName: step6Data.toBankAccount.accountName,
          accountNo: step6Data.toBankAccount.accountNo,
          amountSent: step6Data.toBankAccount.amountSent || 0,
          amountReceived: step6Data.toBankAccount.amountReceived || 0,
        });
      } else {
        setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo(null);
      }
    } else {
      // Reset all step 6 data when no data exists
      setMoveFiatFromTyltToClientPoolAccountRefrenceNumber("");
      setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom(null);
      setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo(null);
    }

    // Initialize step 7 data if exists, otherwise reset to defaults
    if (data.transactionData?.remitFiatToMerchantBankAccount) {
      const step7Data = data.transactionData.remitFiatToMerchantBankAccount;

      // Set reference number
      if (step7Data?.remitFiatToMerchantBankAccountReferenceNumber) {
        setRemitFiatToMerchantBankAccountReferenceNumber(
          step7Data.remitFiatToMerchantBankAccountReferenceNumber,
        );
      } else {
        setRemitFiatToMerchantBankAccountReferenceNumber("");
      }

      // Set from bank account data
      if (step7Data?.fromBankAccount) {
        setRemitFiatToMerchantBankAccountSelectedBankDataFrom({
          bankId: step7Data.fromBankAccount.bankId,
          bankName: step7Data.fromBankAccount.bankName,
          accountName: step7Data.fromBankAccount.accountName,
          accountNo: step7Data.fromBankAccount.accountNo,
          amountSent: step7Data.fromBankAccount.amountSent || 0,
          amountReceived: step7Data.fromBankAccount.amountReceived || 0,
        });
      } else {
        setRemitFiatToMerchantBankAccountSelectedBankDataFrom(null);
      }
    } else {
      // Reset all step 7 data when no data exists
      setRemitFiatToMerchantBankAccountReferenceNumber("");
      setRemitFiatToMerchantBankAccountSelectedBankDataFrom(null);
    }

    if (data.transactionData?.exchangeId) {
      setSelectedExchange(data.transactionData.exchangeId);
    }

    setCurrentStep(data.transactionData?.step || 1);
  }, [data]);

  // Functions for editing and deleting cryptoToFiatList items
  const handleEditTransaction = (index: number) => {
    const transaction = transactionData.cryptoToFiatList[index];
    setEditTransactionData({
      refrenceNumber: transaction.refrenceNumber,
      transactionType: transaction.transactionType,
    });
    setEditingTransactionIndex(index);
    setIsEditSliderOpen(true);
  };

  const handleDeleteTransaction = async (index: number) => {
    try {
      setTransactionLoading(true);

      const updatedList = transactionData.cryptoToFiatList.filter(
        (_, i) => i !== index,
      );

      const transactionDataPayload = {
        exchangeId: selectedExchange!,
        currencySymbol: transactionData.selectedCurrency,
        networkSymbol: transactionData.selectedNetwork,
        cryptoToFiatList: updatedList,
      };

      await invoiceServices.updateInvoiceTransactionData({
        requestId: data.requestId,
        transactionData: transactionDataPayload,
      });

      // Update local state after successful API call
      setTransactionData((prev) => ({
        ...prev,
        cryptoToFiatList: updatedList,
      }));

      // Also update the main data state
      setData((prevData) => ({
        ...prevData,
        transactionData: {
          ...prevData.transactionData,
          cryptoToFiatList: updatedList,
        },
      }));

      handleSuccess({ message: "Transaction deleted successfully" });

      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to delete transaction";
      handleError({ message: errorMessage });
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleUpdateTransaction = async () => {
    if (editingTransactionIndex === null) return;

    try {
      setTransactionLoading(true);

      const updatedList = [...transactionData.cryptoToFiatList];
      updatedList[editingTransactionIndex] = {
        refrenceNumber: editTransactionData.refrenceNumber,
        transactionType: editTransactionData.transactionType,
      };

      const transactionDataPayload = {
        exchangeId: selectedExchange!,
        currencySymbol: transactionData.selectedCurrency,
        networkSymbol: transactionData.selectedNetwork,
        cryptoToFiatList: updatedList,
      };

      await invoiceServices.updateInvoiceTransactionData({
        requestId: data.requestId,
        transactionData: transactionDataPayload,
      });

      // Update local state
      setTransactionData((prev) => ({
        ...prev,
        cryptoToFiatList: updatedList,
      }));

      // Update main data state
      setData((prevData) => ({
        ...prevData,
        transactionData: {
          ...prevData.transactionData,
          cryptoToFiatList: updatedList,
        },
      }));

      setIsEditSliderOpen(false);
      setEditingTransactionIndex(null);
      setEditTransactionData({ refrenceNumber: "", transactionType: "" });

      handleSuccess({ message: "Transaction updated successfully" });

      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to update transaction";
      handleError({ message: errorMessage });
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleCloseEditSlider = () => {
    setIsEditSliderOpen(false);
    setEditingTransactionIndex(null);
    setEditTransactionData({ refrenceNumber: "", transactionType: "" });
  };

  const steps = [
    {
      content: (
        <div className="flex flex-col gap-[10px]">
          <LabelRow
            label="Compliance Status"
            valueComp={<StatusPillRenderer status={data.status} />}
          />
          <LabelRow
            label="Invoice link"
            valueComp={
              <div className="flex items-center gap-2 h-full">
                {data.invoiceLink ? (
                  <div
                    className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                    onClick={() => window.open(data.invoiceLink)}>
                    <span className="material-symbols-outlined text-[14px]">
                      open_in_new
                    </span>
                  </div>
                ) : (
                  <span className="text-text-base text-[12px] font-normal">
                    --
                  </span>
                )}
              </div>
            }
          />
          <LabelRow
            label="Payment Status"
            valueComp={
              data.invoiceDetails?.transactionDetails?.statusDescription ? (
                <DataPill
                  value={
                    data.invoiceDetails.transactionDetails.statusDescription
                  }
                />
              ) : (
                <span className="text-text-base text-[12px] font-normal">
                  --
                </span>
              )
            }
          />
          {/* Submitted Details Card */}
          {data.requestData && (
            <div className="flex flex-col p-[8px] gap-3 bg-bg-page-body rounded-lg">
              <p className="text-text-base text-[14px] font-semibold">
                Submitted Details
              </p>

              {!isEditing && (
                <>
                  {/* Basic Details Section */}
                  <div className="flex flex-col gap-3">
                    <p className="text-text-base text-[12px] font-semibold">
                      Basic Details
                    </p>
                    <LabelRow
                      label="Client Name"
                      value={data.requestData.clientName || "--"}
                    />

                    <LabelRow
                      label="DOB"
                      value={
                        data.requestData.dateOfBirth
                          ? constantFunctions.dateFormatInDDMMYY(
                              data.requestData.dateOfBirth,
                            )
                          : "--"
                      }
                    />

                    <LabelRow
                      label="Mobile"
                      value={data.requestData.mobileNumber || "--"}
                    />

                    <LabelRow
                      label="Email ID"
                      value={data.requestData.email || "--"}
                    />

                    <LabelRow
                      label="Occupation / Sector / Industry"
                      value={data.requestData.occupation || "--"}
                    />

                    <LabelRow
                      label="Company / Employer Name"
                      value={data.requestData.companyName || "--"}
                    />

                    <LabelRow
                      label="Nature of Business"
                      value={data.requestData.natureOfBusiness || "--"}
                    />

                    <LabelRow
                      label="Politically Exposed Person"
                      value={
                        data.requestData.politicallyExposed === true
                          ? "Yes"
                          : data.requestData.politicallyExposed === false
                            ? "No"
                            : "--"
                      }
                    />

                    <LabelRow
                      label="Government ID / Passport"
                      valueComp={
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.governmentIdUrl &&
                              openDocumentInModal(
                                data.requestData.governmentIdUrl,
                                "Government ID / Passport",
                              )
                            }
                            title="View in app">
                            <span className="material-symbols-outlined text-[14px]">
                              visibility
                            </span>
                          </div>
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.governmentIdUrl &&
                              openDocument(data.requestData.governmentIdUrl)
                            }
                            title="Open in new tab">
                            <span className="material-symbols-outlined text-[14px]">
                              open_in_new
                            </span>
                          </div>
                        </div>
                      }
                    />

                    <LabelRow
                      label="Proof of Address"
                      valueComp={
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.proofOfAddressUrl &&
                              openDocumentInModal(
                                data.requestData.proofOfAddressUrl,
                                "Proof of Address",
                              )
                            }
                            title="View in app">
                            <span className="material-symbols-outlined text-[14px]">
                              visibility
                            </span>
                          </div>
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.proofOfAddressUrl &&
                              openDocument(data.requestData.proofOfAddressUrl)
                            }
                            title="Open in new tab">
                            <span className="material-symbols-outlined text-[14px]">
                              open_in_new
                            </span>
                          </div>
                        </div>
                      }
                    />

                    <LabelRow
                      label="Country of Residence"
                      value={data.requestData.countryOfResidence}
                    />

                    <LabelRow
                      label="Country of Nationality"
                      value={data.requestData.countryOfNationality}
                    />

                    <LabelRow
                      label="Country of Birth"
                      value={data.requestData.countryOfBirth}
                    />
                    <LabelRow
                      label="Dual / Other Nationality"
                      value={data.requestData.dualNationality || "--"}
                    />
                  </div>

                  <div className="border-b border-border-subtle"></div>

                  {/* Buyer AML Verification Section */}
                  <div className="flex flex-col gap-3">
                    <p className="text-text-base text-[12px] font-semibold">
                      Buyer AML Verification
                    </p>

                    <LabelRow
                      label="Source of Wealth (SOW)"
                      value={data.requestData.sourceOfWealth}
                    />

                    <LabelRow
                      label="Supporting SOW Documents"
                      valueComp={
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.sowDocumentsUrl &&
                              openDocumentInModal(
                                data.requestData.sowDocumentsUrl,
                                "Supporting SOW Documents",
                              )
                            }
                            title="View in app">
                            <span className="material-symbols-outlined text-[14px]">
                              visibility
                            </span>
                          </div>
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.sowDocumentsUrl &&
                              openDocument(data.requestData.sowDocumentsUrl)
                            }
                            title="Open in new tab">
                            <span className="material-symbols-outlined text-[14px]">
                              open_in_new
                            </span>
                          </div>
                        </div>
                      }
                    />

                    <LabelRow
                      label="Source of Funds (SOF)"
                      value={data.requestData.sourceOfFunds}
                    />

                    <LabelRow
                      label="Supporting SOF Documents"
                      valueComp={
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.sofDocumentsUrl &&
                              openDocumentInModal(
                                data.requestData.sofDocumentsUrl,
                                "Supporting SOF Documents",
                              )
                            }
                            title="View in app">
                            <span className="material-symbols-outlined text-[14px]">
                              visibility
                            </span>
                          </div>
                          <div
                            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                            onClick={() =>
                              data.requestData.sofDocumentsUrl &&
                              openDocument(data.requestData.sofDocumentsUrl)
                            }
                            title="Open in new tab">
                            <span className="material-symbols-outlined text-[14px]">
                              open_in_new
                            </span>
                          </div>
                        </div>
                      }
                    />
                  </div>

                  <div className="border-b border-border-subtle"></div>

                  {/* Transaction & Property Documentation Section */}
                  <div className="flex flex-col gap-3">
                    <p className="text-text-base text-[12px] font-semibold">
                      Transaction & Property Documentation
                    </p>
                    <LabelRow
                      label="Project / Property Name"
                      value={data.requestData.projectName || "--"}
                    />

                    <LabelRow
                      label="Unit Number"
                      value={data.requestData.unitNumber || "--"}
                    />

                    <LabelRow
                      label="Unit Purchase Price"
                      value={
                        data.requestData.unitPurchasePrice
                          ? constantFunctions.formatCurrency(
                              Number(data.requestData.unitPurchasePrice),
                            )
                          : "--"
                      }
                    />
                    <LabelRow
                      label="Purchase Agreement"
                      valueComp={
                        data.requestData.purchaseAgreementUrl ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                              onClick={() =>
                                data.requestData.purchaseAgreementUrl &&
                                openDocumentInModal(
                                  data.requestData.purchaseAgreementUrl,
                                  "Purchase Agreement",
                                )
                              }
                              title="View in app">
                              <span className="material-symbols-outlined text-[14px]">
                                visibility
                              </span>
                            </div>
                            <div
                              className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                              onClick={() =>
                                data.requestData.purchaseAgreementUrl &&
                                openDocument(
                                  data.requestData.purchaseAgreementUrl,
                                )
                              }
                              title="Open in new tab">
                              <span className="material-symbols-outlined text-[14px]">
                                open_in_new
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="material-symbols-outlined text-[14px]">
                            --
                          </span>
                        )
                      }
                    />
                    <LabelRow
                      label="Proof of Property Existence"
                      valueComp={
                        data.requestData.proofOfPropertyUrl ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                              onClick={() =>
                                data.requestData.proofOfPropertyUrl &&
                                openDocumentInModal(
                                  data.requestData.proofOfPropertyUrl,
                                  "Proof of Property Existence",
                                )
                              }
                              title="View in app">
                              <span className="material-symbols-outlined text-[14px]">
                                visibility
                              </span>
                            </div>
                            <div
                              className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                              onClick={() =>
                                data.requestData.proofOfPropertyUrl &&
                                openDocument(
                                  data.requestData.proofOfPropertyUrl,
                                )
                              }
                              title="Open in new tab">
                              <span className="material-symbols-outlined text-[14px]">
                                open_in_new
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="material-symbols-outlined text-[14px]">
                            --
                          </span>
                        )
                      }
                    />
                    <LabelRow
                      label="Invoice Payment Instructions"
                      valueComp={
                        data.requestData.invoicePaymentInstructionUrl ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                              onClick={() =>
                                data.requestData.invoicePaymentInstructionUrl &&
                                openDocumentInModal(
                                  data.requestData.invoicePaymentInstructionUrl,
                                  "Invoice Payment Instructions",
                                )
                              }
                              title="View in app">
                              <span className="material-symbols-outlined text-[14px]">
                                visibility
                              </span>
                            </div>
                            <div
                              className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                              onClick={() =>
                                data.requestData.invoicePaymentInstructionUrl &&
                                openDocument(
                                  data.requestData.invoicePaymentInstructionUrl,
                                )
                              }
                              title="Open in new tab">
                              <span className="material-symbols-outlined text-[14px]">
                                open_in_new
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="material-symbols-outlined text-[14px]">
                            --
                          </span>
                        )
                      }
                    />

                    {/* Additional Documents */}
                    {data.requestData.additionalDocuments &&
                      data.requestData.additionalDocuments.length > 0 && (
                        <div className="mt-4">
                          <div className="text-text-base text-[12px] font-medium mb-2">
                            Additional Documents (
                            {data.requestData.additionalDocuments.length})
                          </div>
                          <div className="space-y-2">
                            {data.requestData.additionalDocuments.map(
                              (doc, index) => (
                                <LabelRow
                                  key={index}
                                  label={doc.name}
                                  valueComp={
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                                        onClick={() =>
                                          openDocumentInModal(doc.url, doc.name)
                                        }
                                        title="View in app">
                                        <span className="material-symbols-outlined text-[14px]">
                                          visibility
                                        </span>
                                      </div>
                                      <div
                                        className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                                        onClick={() => openDocument(doc.url)}
                                        title="Open in new tab">
                                        <span className="material-symbols-outlined text-[14px]">
                                          open_in_new
                                        </span>
                                      </div>
                                    </div>
                                  }
                                />
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                  {!data.invoiceLink && (
                    <div>
                      <div className="border-b border-border-subtle mb-3"></div>
                      <EnhancedNotesSection
                        notes={enhancedNotes}
                        onNotesChange={setEnhancedNotes}
                        requestId={data.requestId}
                      />
                      <div className="flex flex-row gap-5 w-full mt-3">
                        <div className="flex-1">
                          <LightCustomButton
                            text="APPROVE"
                            isActive={actionLoading !== "reject"}
                            onClick={handleApprove}
                            textSize="12px"
                            isButtonLoading={actionLoading === "approve"}
                            className="w-full"
                          />
                        </div>
                        <div className="flex-1">
                          <LightCustomButton
                            text="REJECT"
                            isActive={actionLoading !== "approve"}
                            onClick={handleReject}
                            isButtonLoading={actionLoading === "reject"}
                            textSize="12px"
                            borderColor="border-bg-status-red"
                            buttonColor="bg-bg-status-red"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Additional Info Section */}
              <div className="flex flex-col space-y-[8px] bg-bg-page-body rounded-lg">
                {!isEditing ? (
                  <>
                    <div className="flex-1">
                      <LightCustomButton
                        text="NEED ADDITIONAL INFO"
                        isActive={true}
                        onClick={handleEditToggle}
                        textSize="14px"
                        borderColor="border-border-button-secondary"
                        buttonColor="bg-bg-page-body"
                        className="w-full text-text-button-tertiary"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-text-subtle text-[10px]">
                        If additional information is required, select the above
                        button
                      </p>
                      <p className="text-text-subtle text-[10px]">
                        & also send an email to the user ({data.userEmail}) on
                        what
                      </p>
                      <p className="text-text-subtle text-[10px]">
                        all information is required.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-4">
                    {/* Basic Details Section */}
                    <div className="flex flex-col gap-3">
                      <p className="text-text-base text-[12px] font-semibold">
                        Basic Details
                      </p>
                      <LabelRow
                        label="Client Name"
                        value={editData.clientName || "--"}
                      />

                      <LabelRow
                        label="DOB"
                        value={
                          editData.dateOfBirth
                            ? constantFunctions.dateFormatInDDMMYY(
                                editData.dateOfBirth,
                              )
                            : "--"
                        }
                      />

                      <LabelRow
                        label="Mobile"
                        value={editData.mobileNumber || "--"}
                      />

                      <LabelRow
                        label="Email ID"
                        value={editData.email || "--"}
                      />

                      <LabelRow
                        label="Occupation / Sector / Industry"
                        value={editData.occupation || "--"}
                      />

                      <LabelRow
                        label="Company / Employer Name"
                        value={editData.companyName || "--"}
                      />

                      <LabelRow
                        label="Nature of Business"
                        value={editData.natureOfBusiness || "--"}
                      />

                      <LabelRow
                        label="Politically Exposed Person"
                        value={
                          editData.politicallyExposed === true
                            ? "Yes"
                            : editData.politicallyExposed === false
                              ? "No"
                              : "--"
                        }
                      />

                      <UploadFileComponent
                        text="Government ID / Passport"
                        onFileUpload={(file) =>
                          handleInputChange("governmentIdUrl", file!.url)
                        }
                        fileId="government_id_edit"
                        value={editData.governmentIdUrl}
                      />

                      <UploadFileComponent
                        text="Proof of Address"
                        onFileUpload={(file) =>
                          handleInputChange("proofOfAddressUrl", file!.url)
                        }
                        fileId="proof_of_address_edit"
                        value={editData.proofOfAddressUrl}
                      />

                      {!countriesLoading && countries.length > 0 && (
                        <>
                          <CountriesDropdown
                            listOfValues={countries.map((country) => ({
                              id: country.id,
                              country: country.country,
                              countryImg: country.countryImg!,
                            }))}
                            value={editData.countryOfResidenceId}
                            onChange={(value) =>
                              handleCountrySelection(
                                "countryOfResidence",
                                value,
                              )
                            }
                            label="Country of Residence"
                            color="bg-main-container-header"
                            baseColor="bg-bg-page-body"
                            borderColor="border-subtle"
                            expandedBorderColor="border-button-primary"
                          />

                          <CountriesDropdown
                            listOfValues={countries.map((country) => ({
                              id: country.id,
                              country: country.country,
                              countryImg: country.countryImg!,
                            }))}
                            value={editData.countryOfNationalityId}
                            onChange={(value) =>
                              handleCountrySelection(
                                "countryOfNationality",
                                value,
                              )
                            }
                            label="Country of Nationality"
                            color="bg-main-container-header"
                            baseColor="bg-bg-page-body"
                            borderColor="border-subtle"
                            expandedBorderColor="border-button-primary"
                          />
                          <LabelRow
                            label="Dual / Other Nationality"
                            value={editData.dualNationality || "--"}
                          />

                          <CountriesDropdown
                            listOfValues={countries.map((country) => ({
                              id: country.id,
                              country: country.country,
                              countryImg: country.countryImg!,
                            }))}
                            value={editData.countryOfBirthId}
                            onChange={(value) =>
                              handleCountrySelection("countryOfBirth", value)
                            }
                            label="Country of Birth"
                            color="bg-main-container-header"
                            baseColor="bg-bg-page-body"
                            borderColor="border-subtle"
                            expandedBorderColor="border-button-primary"
                          />
                        </>
                      )}
                    </div>

                    <div className="border-b border-border-subtle"></div>

                    {/* Buyer AML Verification Section */}
                    <div className="flex flex-col gap-3">
                      <p className="text-text-base text-[12px] font-semibold">
                        Buyer AML Verification
                      </p>

                      <MerchantDropdown
                        listOfValues={sowOptions}
                        value={
                          sowOptions.find(
                            (opt) => opt.name === editData.sourceOfWealth,
                          )?.id
                        }
                        onChange={(value) =>
                          handleDropdownSelection(
                            "sourceOfWealth",
                            sowOptions,
                            value,
                          )
                        }
                        label="Source of Wealth (SOW)"
                        color="bg-main-container-header"
                        baseColor="bg-bg-page-body"
                        borderColor="border-subtle"
                        expandedBorderColor="border-button-primary"
                      />

                      <UploadFileComponent
                        text="Supporting SOW Documents"
                        onFileUpload={(file) =>
                          handleInputChange("sowDocumentsUrl", file!.url)
                        }
                        fileId="sow_documents_edit"
                        value={editData.sowDocumentsUrl}
                      />

                      <MerchantDropdown
                        listOfValues={sofOptions}
                        value={
                          sofOptions.find(
                            (opt) => opt.name === editData.sourceOfFunds,
                          )?.id
                        }
                        onChange={(value) =>
                          handleDropdownSelection(
                            "sourceOfFunds",
                            sofOptions,
                            value,
                          )
                        }
                        label="Source of Funds (SOF)"
                        color="bg-main-container-header"
                        baseColor="bg-bg-page-body"
                        borderColor="border-subtle"
                        expandedBorderColor="border-button-primary"
                      />

                      <UploadFileComponent
                        text="Supporting SOF Documents"
                        onFileUpload={(file) =>
                          handleInputChange("sofDocumentsUrl", file!.url)
                        }
                        fileId="sof_documents_edit"
                        value={editData.sofDocumentsUrl}
                      />
                    </div>

                    <div className="border-b border-border-subtle"></div>

                    {/* Transaction & Property Documentation Section */}
                    <div className="flex flex-col gap-3">
                      <p className="text-text-base text-[12px] font-semibold">
                        Transaction & Property Documentation
                      </p>

                      <LabelRow
                        label="Project / Property Name"
                        value={editData.projectName || "--"}
                      />

                      <LabelRow
                        label="Unit Number"
                        value={editData.unitNumber || "--"}
                      />

                      <LabelRow
                        label="Unit Purchase Price"
                        value={
                          editData.unitPurchasePrice
                            ? constantFunctions.formatCurrency(
                                Number(editData.unitPurchasePrice),
                              )
                            : "--"
                        }
                      />

                      <UploadFileComponent
                        text="Purchase Agreement"
                        onFileUpload={(file) =>
                          handleInputChange("purchaseAgreementUrl", file!.url)
                        }
                        fileId="purchase_agreement_edit"
                        value={editData.purchaseAgreementUrl}
                      />

                      <UploadFileComponent
                        text="Proof of Property Existence"
                        onFileUpload={(file) =>
                          handleInputChange("proofOfPropertyUrl", file!.url)
                        }
                        fileId="proof_of_property_edit"
                        value={editData.proofOfPropertyUrl}
                      />

                      <UploadFileComponent
                        text="Invoice Payment Instructions"
                        onFileUpload={(file) =>
                          handleInputChange(
                            "invoicePaymentInstructionUrl",
                            file!.url,
                          )
                        }
                        fileId="invoice_payment_instruction_edit"
                        value={editData.invoicePaymentInstructionUrl}
                      />
                    </div>

                    <div className="border-b border-border-subtle"></div>

                    <div className="flex flex-row gap-5 w-full">
                      <div className="flex-1">
                        <LightCustomButton
                          text="CANCEL"
                          onClick={() => setIsEditing(false)}
                          buttonColor="bg-bg-page-body"
                          borderColor="border-border-button-delete"
                          isActive={true}
                          className="w-full"
                        />
                      </div>
                      <div className="flex-1">
                        <LightCustomButton
                          text="SAVE"
                          onClick={handleUpdateInvoiceDetails}
                          isActive={!updateLoading}
                          isButtonLoading={updateLoading}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ),
      validator: () => true,
      ctaText: "NEXT",
    },
    {
      content: (
        <div className="flex flex-col gap-[10px]">
          <LabelRow
            label="Invoice link"
            valueComp={
              <div className="flex items-center gap-2 h-full">
                {data.invoiceLink ? (
                  <div
                    className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                    onClick={() => window.open(data.invoiceLink)}>
                    <span className="material-symbols-outlined text-[14px]">
                      open_in_new
                    </span>
                  </div>
                ) : (
                  <span className="text-text-base text-[12px] font-normal">
                    --
                  </span>
                )}
              </div>
            }
          />
          {data.transactionData && data.transactionData.offrampTransfer && (
            <>
              <LabelRow
                label="Transfer to Off Ramp A/C"
                valueComp={
                  <StatusPillRenderer
                    status={data.transactionData.offrampTransfer?.status}
                  />
                }
              />
              <LabelRow
                label="Off Ramp To Txn ID"
                value={
                  data.transactionData.offrampTransfer?.offrampToTransactionId.toString() ||
                  "--"
                }
                isCopy={true}
              />
              <LabelRow
                label="Off Ramp Order ID"
                value={
                  data.transactionData.offrampTransfer?.offrampFromTransactionId?.toString() ||
                  "--"
                }
                isCopy={true}
              />
              <LabelRow
                label="Merchant Amount Received"
                value={
                  data.invoiceDetails?.transactionDetails?.settledAmountReceived?.toString() ||
                  "--"
                }
              />
              <LabelRow
                label="Fees"
                value={
                  data.invoiceDetails?.transactionDetails?.commission?.toString() ||
                  "--"
                }
              />
              <LabelRow
                label="Amount Transferred"
                value={
                  data.transactionData.offrampTransfer?.amount.toString() ||
                  "--"
                }
              />
              <LabelRow
                label="Currency | Network"
                value={
                  `${data.transactionData.offrampTransfer?.currency} | TPNK` ||
                  "--"
                }
              />
            </>
          )}
          <EnhancedNotesSection
            notes={enhancedNotes}
            onNotesChange={setEnhancedNotes}
            requestId={data.requestId}
          />
        </div>
      ),
      validator: () => data.transactionData?.offrampTransfer !== undefined,
      ctaText: "NEXT",
      label: "Internal Off-ramp",
    },
    {
      content: (
        <div className="flex flex-col gap-[16px]">
          {!exchangesLoading && exchanges.length > 0 && (
            <MerchantDropdown
              listOfValues={getUniqueExchanges()}
              value={
                getUniqueExchanges().find(
                  (ex) => ex.name === selectedExchangeName,
                )?.id
              }
              onChange={handleStep3ExchangeSelection}
              label="Select Exchange"
              color="bg-main-container-header"
              baseColor="bg-bg-page-body"
              borderColor="border-subtle"
              expandedBorderColor="border-button-primary"
            />
          )}

          {selectedExchangeName && availableNetworks.length > 0 && (
            <MerchantDropdown
              listOfValues={availableNetworks.map((network, index) => ({
                id: index + 1,
                name: network,
              }))}
              value={
                availableNetworks.findIndex(
                  (net) => net === selectedNetworkName,
                ) + 1 || undefined
              }
              onChange={handleStep3NetworkSelection}
              label="Select Network"
              color="bg-main-container-header"
              baseColor="bg-bg-page-body"
              borderColor="border-subtle"
              expandedBorderColor="border-button-primary"
            />
          )}
          <div className="border-b border-border-subtle"></div>
          {data.transactionData && data.transactionData.offrampTransfer && (
            <>
              <LabelRow
                label="Amount"
                value={
                  data.transactionData.offrampTransfer?.amount.toString() ||
                  "--"
                }
              />
              <LabelRow
                label="Currency"
                value={data.transactionData.offrampTransfer?.currency || "--"}
              />
              {data.transactionData.offrampTransfer?.orderId && (
                <LabelRow
                  label="Txn Order ID"
                  value={
                    data.transactionData.settlement?.exchangeTransfer?.orderId.toString() ||
                    "--"
                  }
                  isCopy={true}
                  alignment="col"
                />
              )}
              {/* <LabelRow label="Txn Hash" value={"--"} /> */}
            </>
          )}
          <div className="flex-1 w-full">
            <LightCustomButton
              text="SEND"
              onClick={() => handleUpdateTransactionData(true, 4)}
              isActive={!transactionLoading && selectedExchange !== undefined}
              isButtonLoading={transactionLoading}
              className="w-full"
            />
          </div>
          <EnhancedNotesSection
            notes={enhancedNotes}
            onNotesChange={setEnhancedNotes}
            requestId={data.requestId}
          />
        </div>
      ),
      validator: () => true,
      ctaText: "NEXT",
      label: "Off-Ramp Account to Exchange",
    },
    {
      content: (
        <div className="flex flex-col gap-[16px]">
          <GlobalTextInputField
            title="Reference Number"
            placeholder="Enter reference number"
            value={newTransaction.refrenceNumber}
            onChange={(e) =>
              setNewTransaction((prev) => ({
                ...prev,
                refrenceNumber: e.target.value,
              }))
            }
          />

          <MerchantDropdown
            listOfValues={transactionTypesOptions}
            value={
              transactionTypesOptions.find(
                (opt) => opt.name === newTransaction.transactionType,
              )?.id
            }
            onChange={(value) => {
              const selectedId = Number(value);
              const option = transactionTypesOptions.find(
                (opt) => opt.id === selectedId,
              );
              setNewTransaction((prev) => ({
                ...prev,
                transactionType: option?.name || "",
              }));
            }}
            label="Transaction Type"
            color="bg-main-container-header"
            baseColor="bg-bg-page-body"
            borderColor="border-subtle"
            expandedBorderColor="border-button-primary"
          />

          {/* Display existing crypto to fiat transactions */}
          {transactionData.cryptoToFiatList.length > 0 && (
            <div className="border border-border-subtle rounded-[8px] flex flex-col">
              <div className="rounded-t-[8px] bg-bg-page-body px-[8px] py-[8px] flex flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "15%" }}>
                  Trx #
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "25%" }}>
                  Trx Type
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "40%" }}>
                  Trx Ref
                </span>
                <span
                  className="flex font-semibold text-[10px] text-text-base justify-center"
                  style={{ width: "20%" }}>
                  Actions
                </span>
              </div>
              <div className="flex flex-col">
                {transactionData.cryptoToFiatList.map((transaction, index) => (
                  <div
                    key={index}
                    className="px-[8px] py-[8px] flex bg-bg-page-body flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                    <span
                      className="flex text-[8px] text-text-base items-center"
                      style={{ width: "15%" }}>
                      {index + 1}
                    </span>
                    <span
                      className="flex text-[8px] text-text-base items-center"
                      style={{ width: "35%" }}>
                      {transaction.transactionType}
                    </span>
                    <div
                      className="flex flex-row gap-1 items-center"
                      style={{ width: "40%" }}>
                      <span className="flex text-[8px] text-text-base max-w-[120px] truncate">
                        {transaction.refrenceNumber}
                      </span>
                      <CopyButton
                        copyText={transaction.refrenceNumber}
                        copyMessage="Transaction hash copied"
                        copyIconSize={10}
                        buttonSize="16px"
                      />
                    </div>
                    <div
                      className="flex flex-row gap-1 items-center justify-center"
                      style={{ width: "20%" }}>
                      <button
                        onClick={() => handleEditTransaction(index)}
                        className="flex items-center justify-center p-1 text-text-button-tertiary">
                        <span className="material-symbols-outlined text-[12px]">
                          edit
                        </span>
                      </button>
                      <button
                        onClick={() => handleDeleteTransaction(index)}
                        className="flex items-center justify-center p-1 text-text-status-red">
                        <span className="material-symbols-outlined text-[12px]">
                          delete
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 w-full">
            <LightCustomButton
              text="SAVE"
              onClick={() => handleUpdateTransactionData(false, 5)}
              isActive={
                !transactionLoading &&
                newTransaction.refrenceNumber !== "" &&
                newTransaction.transactionType !== ""
              }
              isButtonLoading={transactionLoading}
              className="w-full"
            />
          </div>
          <EnhancedNotesSection
            notes={enhancedNotes}
            onNotesChange={setEnhancedNotes}
            requestId={data.requestId}
          />
        </div>
      ),
      ctaText: "NEXT",
      label: "Convert Crypto to Fiat",
      validator: () => true,
    },
    {
      content: (
        <div className="flex flex-col gap-[16px]">
          <GlobalTextInputField
            title="Reference Number"
            placeholder="Enter withdrawal reference number"
            value={
              withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountReferenceNumber
            }
            onChange={(e) =>
              setWithdrawFiatToTyltBankAccount((prev) => ({
                ...prev,
                withdrawFiatToTyltBankAccountReferenceNumber: e.target.value,
              }))
            }
          />

          {!exchangesLoading && exchanges.length > 0 && (
            <MerchantDropdown
              listOfValues={exchanges.map((exchange) => ({
                id: exchange.exchangeId,
                name: exchange.exchange,
              }))}
              value={
                exchanges.find(
                  (ex) =>
                    ex.exchange ===
                    withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountExchange,
                )?.exchangeId
              }
              onChange={(value) => {
                const selectedId = Number(value);
                const selectedExchange = exchanges.find(
                  (ex) => ex.exchangeId === selectedId,
                );
                setWithdrawFiatToTyltBankAccount((prev) => ({
                  ...prev,
                  withdrawFiatToTyltBankAccountExchange:
                    selectedExchange?.exchange || "",
                }));
              }}
              label="Select Exchange"
              color="bg-main-container-header"
              baseColor="bg-bg-page-body"
              borderColor="border-subtle"
              expandedBorderColor="border-button-primary"
            />
          )}
          <div className="border-b border-border-subtle"></div>
          <BankAccountSelectionComponent
            onBankSelection={(bankData) =>
              bankData
                ? setwithdrawFiatToTyltBankAccountSelectedBankData((prev) => ({
                    ...bankData,
                    amountSent: prev?.amountSent || 0,
                    amountReceived: prev?.amountReceived || 0,
                  }))
                : setwithdrawFiatToTyltBankAccountSelectedBankData(null)
            }
            selectedBankId={
              withdrawFiatToTyltBankAccountselectedBankData?.bankId
            }
            label="To:"
          />
          {withdrawFiatToTyltBankAccountselectedBankData && (
            <div className="flex gap-4">
              <GlobalTextInputField
                title="Amount Sent"
                placeholder="Enter amount sent"
                type="number"
                value={withdrawFiatToTyltBankAccountselectedBankData.amountSent.toString()}
                onChange={(e) =>
                  setwithdrawFiatToTyltBankAccountSelectedBankData((prev) =>
                    prev
                      ? { ...prev, amountSent: Number(e.target.value) }
                      : null,
                  )
                }
              />
              <GlobalTextInputField
                title="Amount Received"
                placeholder="Enter amount received"
                type="number"
                value={withdrawFiatToTyltBankAccountselectedBankData.amountReceived.toString()}
                onChange={(e) =>
                  setwithdrawFiatToTyltBankAccountSelectedBankData((prev) =>
                    prev
                      ? { ...prev, amountReceived: Number(e.target.value) }
                      : null,
                  )
                }
              />
            </div>
          )}
          <div className="border-b border-border-subtle"></div>
          {transactionData.withdrawFiatToTyltBankAccount.toBankAccount && (
            <div className="border border-border-subtle rounded-[8px] flex flex-col">
              <div className="rounded-t-[8px] bg-bg-page-body px-[8px] py-[8px] flex flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "15%" }}>
                  ID
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "25%" }}>
                  Bank Name
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "15%" }}>
                  Name
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "20%" }}>
                  Number
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "12.5%" }}>
                  Amt Sent
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "12.5%" }}>
                  Amt Received
                </span>
              </div>
              <div className="flex flex-col">
                <div className="px-[8px] py-[8px] flex bg-bg-page-body flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "15%" }}>
                    {withdrawFiatToTyltBankAccountselectedBankData?.bankId}
                  </span>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "25%" }}>
                    {withdrawFiatToTyltBankAccountselectedBankData?.bankName}
                  </span>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "15%" }}>
                    {withdrawFiatToTyltBankAccountselectedBankData?.accountName}
                  </span>
                  <div
                    className="flex flex-col gap-1 justify-center"
                    style={{ width: "20%" }}>
                    <span className="flex text-[8px] text-text-base break-all leading-tight">
                      {withdrawFiatToTyltBankAccountselectedBankData?.accountNo}
                    </span>
                    <CopyButton
                      copyText={
                        withdrawFiatToTyltBankAccountselectedBankData?.accountNo.toString() ||
                        ""
                      }
                      copyMessage="Bank account number copied"
                      copyIconSize={10}
                      buttonSize="16px"
                    />
                  </div>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "12.5%" }}>
                    {withdrawFiatToTyltBankAccountselectedBankData?.amountSent ||
                      0}
                  </span>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "12.5%" }}>
                    {withdrawFiatToTyltBankAccountselectedBankData?.amountReceived ||
                      0}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 w-full">
            <LightCustomButton
              text="SAVE"
              onClick={() => handleUpdateTransactionData(false, 6)}
              isActive={
                !transactionLoading &&
                withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountReferenceNumber !==
                  "" &&
                withdrawFiatToTyltBankAccount.withdrawFiatToTyltBankAccountExchange !==
                  "" &&
                withdrawFiatToTyltBankAccountselectedBankData !== null
              }
              isButtonLoading={transactionLoading}
              className="w-full"
            />
          </div>
          <EnhancedNotesSection
            notes={enhancedNotes}
            onNotesChange={setEnhancedNotes}
            requestId={data.requestId}
          />
        </div>
      ),
      ctaText: "NEXT",
      validator: () => true,
      label: "Withdrawn Fiat to Tylt Bank Account",
    },
    {
      content: (
        <div className="flex flex-col gap-[16px]">
          <GlobalTextInputField
            title="Reference Number"
            placeholder="Enter transfer reference number"
            value={moveFiatFromTyltToClientPoolAccountRefrenceNumber}
            onChange={(e) =>
              setMoveFiatFromTyltToClientPoolAccountRefrenceNumber(
                e.target.value,
              )
            }
          />
          <div className="border-b border-border-subtle"></div>
          <BankAccountSelectionComponent
            onBankSelection={(bankData) =>
              bankData
                ? setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom(
                    (prev) => ({
                      ...bankData,
                      amountSent: prev?.amountSent || 0,
                      amountReceived: prev?.amountReceived || 0,
                    }),
                  )
                : setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom(
                    null,
                  )
            }
            selectedBankId={
              moveFiatFromTyltToClientPoolAccountselectedBankDataFrom?.bankId
            }
            label="From:"
          />
          {moveFiatFromTyltToClientPoolAccountselectedBankDataFrom && (
            <div className="flex gap-4">
              <GlobalTextInputField
                title="Amount Sent"
                placeholder="Enter amount sent"
                type="number"
                value={moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.amountSent.toString()}
                onChange={(e) =>
                  setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom(
                    (prev) =>
                      prev
                        ? { ...prev, amountSent: Number(e.target.value) }
                        : null,
                  )
                }
              />
              <GlobalTextInputField
                title="Amount Received"
                placeholder="Enter amount received"
                type="number"
                value={moveFiatFromTyltToClientPoolAccountselectedBankDataFrom.amountReceived.toString()}
                onChange={(e) =>
                  setMoveFiatFromTyltToClientPoolAccountSelectedBankDataFrom(
                    (prev) =>
                      prev
                        ? { ...prev, amountReceived: Number(e.target.value) }
                        : null,
                  )
                }
              />
            </div>
          )}

          <BankAccountSelectionComponent
            onBankSelection={(bankData) =>
              bankData
                ? setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo(
                    (prev) => ({
                      ...bankData,
                      amountSent: prev?.amountSent || 0,
                      amountReceived: prev?.amountReceived || 0,
                    }),
                  )
                : setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo(null)
            }
            selectedBankId={
              moveFiatFromTyltToClientPoolAccountselectedBankDataTo?.bankId
            }
            label="To:"
          />
          {moveFiatFromTyltToClientPoolAccountselectedBankDataTo && (
            <div className="flex gap-4">
              <GlobalTextInputField
                title="Amount Sent"
                placeholder="Enter amount sent"
                type="number"
                value={moveFiatFromTyltToClientPoolAccountselectedBankDataTo.amountSent.toString()}
                onChange={(e) =>
                  setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo(
                    (prev) =>
                      prev
                        ? { ...prev, amountSent: Number(e.target.value) }
                        : null,
                  )
                }
              />
              <GlobalTextInputField
                title="Amount Received"
                placeholder="Enter amount received"
                type="number"
                value={moveFiatFromTyltToClientPoolAccountselectedBankDataTo.amountReceived.toString()}
                onChange={(e) =>
                  setMoveFiatFromTyltToClientPoolAccountSelectedBankDataTo(
                    (prev) =>
                      prev
                        ? { ...prev, amountReceived: Number(e.target.value) }
                        : null,
                  )
                }
              />
            </div>
          )}
          <div className="border-b border-border-subtle"></div>
          {data.transactionData?.moveFiatFromTyltToClientPoolAccount && (
            <div className="border border-border-subtle rounded-[8px] flex flex-col">
              <div className="rounded-t-[8px] bg-bg-page-body px-[8px] py-[8px] flex flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "15%" }}>
                  Flow
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "20%" }}>
                  Bank Name
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "20%" }}>
                  Account Name
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "20%" }}>
                  Account Number
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "12.5%" }}>
                  Amt Sent
                </span>
                <span
                  className="font-semibold text-[10px] text-text-base"
                  style={{ width: "12.5%" }}>
                  Amt Received
                </span>
              </div>
              {data.transactionData?.moveFiatFromTyltToClientPoolAccount && (
                <div className="flex flex-col">
                  {data.transactionData.moveFiatFromTyltToClientPoolAccount
                    ?.fromBankAccount && (
                    <div className="px-[8px] py-[8px] flex bg-bg-page-body flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                      <span
                        className="flex text-[8px] text-text-base items-center font-medium"
                        style={{ width: "15%" }}>
                        From
                      </span>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "20%" }}>
                        {
                          data.transactionData
                            ?.moveFiatFromTyltToClientPoolAccount
                            ?.fromBankAccount?.bankName
                        }
                      </span>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "20%" }}>
                        {
                          data.transactionData
                            ?.moveFiatFromTyltToClientPoolAccount
                            ?.fromBankAccount?.accountName
                        }
                      </span>
                      <div
                        className="flex flex-col gap-1 justify-center"
                        style={{ width: "20%" }}>
                        <span className="flex text-[8px] text-text-base break-all leading-tight">
                          {
                            data.transactionData
                              ?.moveFiatFromTyltToClientPoolAccount
                              ?.fromBankAccount?.accountNo
                          }
                        </span>
                        <CopyButton
                          copyText={
                            data.transactionData?.moveFiatFromTyltToClientPoolAccount?.fromBankAccount?.accountNo?.toString() ||
                            ""
                          }
                          copyMessage="From account number copied"
                          copyIconSize={10}
                          buttonSize="16px"
                        />
                      </div>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "12.5%" }}>
                        {data.transactionData
                          ?.moveFiatFromTyltToClientPoolAccount?.fromBankAccount
                          ?.amountSent || 0}
                      </span>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "12.5%" }}>
                        {data.transactionData
                          ?.moveFiatFromTyltToClientPoolAccount?.fromBankAccount
                          ?.amountReceived || 0}
                      </span>
                    </div>
                  )}
                  {data.transactionData?.moveFiatFromTyltToClientPoolAccount
                    ?.toBankAccount && (
                    <div className="px-[8px] py-[8px] flex bg-bg-page-body flex-row justify-start flex-shrink-0">
                      <span
                        className="flex text-[8px] text-text-base items-center font-medium"
                        style={{ width: "15%" }}>
                        To
                      </span>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "20%" }}>
                        {
                          data.transactionData
                            ?.moveFiatFromTyltToClientPoolAccount?.toBankAccount
                            ?.bankName
                        }
                      </span>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "20%" }}>
                        {
                          data.transactionData
                            ?.moveFiatFromTyltToClientPoolAccount?.toBankAccount
                            ?.accountName
                        }
                      </span>
                      <div
                        className="flex flex-col gap-1 justify-center"
                        style={{ width: "20%" }}>
                        <span className="flex text-[8px] text-text-base break-all leading-tight">
                          {
                            data.transactionData
                              ?.moveFiatFromTyltToClientPoolAccount
                              ?.toBankAccount?.accountNo
                          }
                        </span>
                        <CopyButton
                          copyText={
                            data.transactionData?.moveFiatFromTyltToClientPoolAccount?.toBankAccount?.accountNo?.toString() ||
                            ""
                          }
                          copyMessage="To account number copied"
                          copyIconSize={10}
                          buttonSize="16px"
                        />
                      </div>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "12.5%" }}>
                        {data.transactionData
                          ?.moveFiatFromTyltToClientPoolAccount?.toBankAccount
                          ?.amountSent || 0}
                      </span>
                      <span
                        className="flex text-[8px] text-text-base items-center"
                        style={{ width: "12.5%" }}>
                        {data.transactionData
                          ?.moveFiatFromTyltToClientPoolAccount?.toBankAccount
                          ?.amountReceived || 0}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex-1 w-full">
            <LightCustomButton
              text="SAVE"
              onClick={() => handleUpdateTransactionData(false, 7)}
              isActive={
                !transactionLoading &&
                moveFiatFromTyltToClientPoolAccountRefrenceNumber !== "" &&
                moveFiatFromTyltToClientPoolAccountselectedBankDataFrom !==
                  null &&
                moveFiatFromTyltToClientPoolAccountselectedBankDataTo !== null
              }
              isButtonLoading={transactionLoading}
              className="w-full"
            />
          </div>
          <EnhancedNotesSection
            notes={enhancedNotes}
            onNotesChange={setEnhancedNotes}
            requestId={data.requestId}
          />
        </div>
      ),
      ctaText: "NEXT",
      validator: () => true,
      label: "Move Fiat Tylt Bank Account to Tylt Client Pool Account",
    },
    {
      content: (
        <div className="flex flex-col gap-[16px]">
          <GlobalTextInputField
            title="Reference Number"
            placeholder="Enter remittance reference number"
            value={remitFiatToMerchantBankAccountReferenceNumber}
            onChange={(e) =>
              setRemitFiatToMerchantBankAccountReferenceNumber(e.target.value)
            }
          />
          <div className="border-b border-border-subtle"></div>
          <BankAccountSelectionComponent
            onBankSelection={(bankData) =>
              bankData
                ? setRemitFiatToMerchantBankAccountSelectedBankDataFrom(
                    (prev) => ({
                      ...bankData,
                      amountSent: prev?.amountSent || 0,
                      amountReceived: prev?.amountReceived || 0,
                    }),
                  )
                : setRemitFiatToMerchantBankAccountSelectedBankDataFrom(null)
            }
            selectedBankId={
              remitFiatToMerchantBankAccountSelectedBankDataFrom?.bankId
            }
            label="From:"
          />
          {remitFiatToMerchantBankAccountSelectedBankDataFrom && (
            <div className="flex gap-4">
              <GlobalTextInputField
                title="Amount Sent"
                placeholder="Enter amount sent"
                type="number"
                value={remitFiatToMerchantBankAccountSelectedBankDataFrom.amountSent.toString()}
                onChange={(e) =>
                  setRemitFiatToMerchantBankAccountSelectedBankDataFrom(
                    (prev) =>
                      prev
                        ? { ...prev, amountSent: Number(e.target.value) }
                        : null,
                  )
                }
              />
              <GlobalTextInputField
                title="Amount Received"
                placeholder="Enter amount received"
                type="number"
                value={remitFiatToMerchantBankAccountSelectedBankDataFrom.amountReceived.toString()}
                onChange={(e) =>
                  setRemitFiatToMerchantBankAccountSelectedBankDataFrom(
                    (prev) =>
                      prev
                        ? { ...prev, amountReceived: Number(e.target.value) }
                        : null,
                  )
                }
              />
            </div>
          )}

          {data.transactionData?.remitFiatToMerchantBankAccount
            ?.fromBankAccount && (
            <div className="flex flex-col">
              <div className="px-[8px] py-[12px] flex bg-bg-page-body flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                <span
                  className="flex text-[10px] text-text-base items-center font-bold"
                  style={{ width: "15%" }}>
                  Type
                </span>
                <span
                  className="flex text-[10px] text-text-base items-center font-bold"
                  style={{ width: "20%" }}>
                  Bank
                </span>
                <span
                  className="flex text-[10px] text-text-base items-center font-bold"
                  style={{ width: "20%" }}>
                  Account Name
                </span>
                <span
                  className="flex text-[10px] text-text-base items-center font-bold"
                  style={{ width: "20%" }}>
                  Account Number
                </span>
                <span
                  className="flex text-[10px] text-text-base items-center font-bold"
                  style={{ width: "12.5%" }}>
                  Amt Sent
                </span>
                <span
                  className="flex text-[10px] text-text-base items-center font-bold"
                  style={{ width: "12.5%" }}>
                  Amt Received
                </span>
              </div>

              {data.transactionData?.remitFiatToMerchantBankAccount
                ?.fromBankAccount && (
                <div className="px-[8px] py-[8px] flex bg-bg-page-body flex-row justify-start flex-shrink-0 border-b border-b-border-subtle">
                  <span
                    className="flex text-[8px] text-text-base items-center font-medium"
                    style={{ width: "15%" }}>
                    From
                  </span>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "20%" }}>
                    {
                      data.transactionData?.remitFiatToMerchantBankAccount
                        ?.fromBankAccount?.bankName
                    }
                  </span>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "20%" }}>
                    {
                      data.transactionData?.remitFiatToMerchantBankAccount
                        ?.fromBankAccount?.accountName
                    }
                  </span>
                  <div
                    className="flex flex-col gap-1 justify-center"
                    style={{ width: "20%" }}>
                    <span className="flex text-[8px] text-text-base break-all leading-tight">
                      {
                        data.transactionData?.remitFiatToMerchantBankAccount
                          ?.fromBankAccount?.accountNo
                      }
                    </span>
                    <CopyButton
                      copyText={
                        data.transactionData?.remitFiatToMerchantBankAccount?.fromBankAccount?.accountNo?.toString() ||
                        ""
                      }
                      copyMessage="From account number copied"
                      copyIconSize={10}
                      buttonSize="16px"
                    />
                  </div>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "12.5%" }}>
                    {data.transactionData?.remitFiatToMerchantBankAccount
                      ?.fromBankAccount?.amountSent || 0}
                  </span>
                  <span
                    className="flex text-[8px] text-text-base items-center"
                    style={{ width: "12.5%" }}>
                    {data.transactionData?.remitFiatToMerchantBankAccount
                      ?.fromBankAccount?.amountReceived || 0}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="text-[10px] font-semibold text-text-base">To:</div>
          {data.bankAccountDetails && (
            <div className="flex flex-col gap-2 border border-border-subtle rounded-[8px] bg-bg-page-body p-2">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() =>
                  setBankAccountDetailsVisible(!bankAccountDetailsVisible)
                }>
                <span className="text-text-base text-[12px] font-normal">
                  Settlement Bank Account
                </span>
                <span
                  className="material-symbols-outlined cursor-pointer"
                  style={{
                    transform: bankAccountDetailsVisible
                      ? "rotate(180deg)"
                      : "",
                    transition: "transform 0.3s ease",
                  }}>
                  keyboard_arrow_down
                </span>
              </div>
              {bankAccountDetailsVisible && (
                <div className="flex flex-col gap-2 w-full">
                  <div className="border border-border-subtle rounded-[8px] bg-bg-page-body">
                    <div className="px-[12px] py-[8px] space-y-[8px]">
                      <LabelRow
                        label="Account Holder"
                        value={
                          data.bankAccountDetails.accountHolderName || "--"
                        }
                      />
                      <LabelRow
                        label="Alias"
                        value={data.bankAccountDetails.aliasName || "--"}
                      />
                      {data.bankAccountDetails.accountNumber && (
                        <LabelRow
                          label="Account Number"
                          value={data.bankAccountDetails.accountNumber}
                          isCopy={true}
                        />
                      )}
                      {data.bankAccountDetails.isIbanAvailable &&
                        data.bankAccountDetails.iban && (
                          <LabelRow
                            label="IBAN"
                            value={data.bankAccountDetails.iban}
                            isCopy={true}
                          />
                        )}
                      {data.bankAccountDetails.routingCode && (
                        <LabelRow
                          label="Routing Code"
                          value={data.bankAccountDetails.routingCode}
                          isCopy={true}
                        />
                      )}
                      {data.bankAccountDetails.country && (
                        <LabelRow
                          label="Country"
                          value={data.bankAccountDetails.country}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex-1 w-full">
            <LightCustomButton
              text="SAVE"
              onClick={() => handleUpdateTransactionData(false, 7)}
              isActive={
                !transactionLoading &&
                remitFiatToMerchantBankAccountReferenceNumber !== "" &&
                remitFiatToMerchantBankAccountSelectedBankDataFrom !== null
              }
              isButtonLoading={transactionLoading}
              className="w-full"
            />
          </div>
          <EnhancedNotesSection
            notes={enhancedNotes}
            onNotesChange={setEnhancedNotes}
            requestId={data.requestId}
          />
        </div>
      ),
      validator: () => true,
      label: "Fiat is Remitted to the Merchant Bank Account",
    },
  ];

  return (
    <div className="flex flex-col gap-[12px]">
      {/* Header Card */}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
            Purpose: Real Estate
          </div>
        </div>
        <LabelRow
          label="Off-Ramp Currency"
          valueComp={
            <div className="flex flex-row gap-1 items-center">
              <img
                src={data.settlementCurrencyImage}
                alt={data.settlementCurrencySymbol}
                className="w-[16px] h-[16px] rounded"
              />
              <span className="text-text-base text-[12px] font-normal">
                {data.settlementCurrencySymbol}
              </span>
            </div>
          }
        />
        <LabelRow
          label="Base Currency | Amount"
          valueComp={
            <div className="flex flex-row gap-1 items-center">
              <img
                src={data.baseCurrencyImage}
                alt={data.baseCurrencySymbol}
                className="w-[16px] h-[16px] rounded"
              />
              <span className="text-text-base text-[12px] font-normal">
                {data.baseCurrencySymbol}
              </span>
              <span className="text-text-base text-[12px] font-normal">|</span>
              <span className="text-text-base text-[12px] font-normal">
                {constantFunctions.formatCurrency(
                  data.requestData?.baseAmount!,
                )}
              </span>
            </div>
          }
        />
        {data.bankAccountDetails && (
          <div className="flex flex-col gap-2 border border-border-subtle rounded-[8px] bg-bg-page-body p-2">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() =>
                setBankAccountDetailsVisible(!bankAccountDetailsVisible)
              }>
              <span className="text-text-base text-[12px] font-normal">
                Settlement Bank Account
              </span>
              <span
                className="material-symbols-outlined cursor-pointer"
                style={{
                  transform: bankAccountDetailsVisible ? "rotate(180deg)" : "",
                  transition: "transform 0.3s ease",
                }}>
                keyboard_arrow_down
              </span>
            </div>
            {bankAccountDetailsVisible && (
              <div className="flex flex-col gap-2 w-full">
                <div className="border border-border-subtle rounded-[8px] bg-bg-page-body">
                  <div className="px-[12px] py-[8px] space-y-[8px]">
                    <LabelRow
                      label="Account Holder"
                      value={data.bankAccountDetails.accountHolderName || "--"}
                    />
                    <LabelRow
                      label="Alias"
                      value={data.bankAccountDetails.aliasName || "--"}
                    />
                    {data.bankAccountDetails.accountNumber && (
                      <LabelRow
                        label="Account Number"
                        value={data.bankAccountDetails.accountNumber}
                        isCopy={true}
                      />
                    )}
                    {data.bankAccountDetails.isIbanAvailable &&
                      data.bankAccountDetails.iban && (
                        <LabelRow
                          label="IBAN"
                          value={data.bankAccountDetails.iban}
                          isCopy={true}
                        />
                      )}
                    {data.bankAccountDetails.routingCode && (
                      <LabelRow
                        label="Routing Code"
                        value={data.bankAccountDetails.routingCode}
                        isCopy={true}
                      />
                    )}
                    {data.bankAccountDetails.country && (
                      <LabelRow
                        label="Country"
                        value={data.bankAccountDetails.country}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <LabelRow
          label="Invoice ID | Request ID"
          valueComp={
            <div className="flex flex-row gap-1 items-center">
              <span className="text-text-base text-[12px] font-normal">
                {data.invoiceId ?? "- -"}
              </span>
              {data.invoiceId && (
                <CopyButton
                  copyText={data.invoiceId || ""}
                  copyMessage={`Invoice ID copied to clipboard`}
                />
              )}
              <span className="text-text-base text-[12px] font-normal">|</span>
              <span className="text-text-base text-[12px] font-normal">
                {data.requestId}
              </span>
              <CopyButton
                copyText={data.requestId ? data.requestId.toString() : ""}
                copyMessage={`Request ID copied to clipboard`}
              />
            </div>
          }
        />
        {data.invoiceDetails?.transactionDetails && (
          <LabelRow
            label="Payment Details"
            valueComp={
              <div className="flex items-center gap-2 h-full">
                <div
                  className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                  onClick={() => setPaymentDetailsOpen(true)}>
                  <span className="material-symbols-outlined text-[14px]">
                    open_in_new
                  </span>
                </div>
              </div>
            }
          />
        )}
      </div>

      <div className="flex flex-col py-[12px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <HorizontalStepper steps={steps} currentStep={currentStep} />
      </div>

      {/* Edit Transaction Slider */}
      <SlidingPanel
        isOpen={isEditSliderOpen}
        onClose={handleCloseEditSlider}
        heading="Edit Transaction"
        showHeader={true}
        isSlider={true}
        widthDesktop="400px">
        <div className="flex flex-col gap-4 p-4 mt-[64px]">
          <GlobalTextInputField
            title="Reference Number"
            placeholder="Enter reference number"
            value={editTransactionData.refrenceNumber}
            onChange={(e) =>
              setEditTransactionData((prev) => ({
                ...prev,
                refrenceNumber: e.target.value,
              }))
            }
          />

          <MerchantDropdown
            listOfValues={transactionTypesOptions}
            value={
              transactionTypesOptions.find(
                (opt) => opt.name === editTransactionData.transactionType,
              )?.id
            }
            onChange={(value) => {
              const selectedId = Number(value);
              const option = transactionTypesOptions.find(
                (opt) => opt.id === selectedId,
              );
              setEditTransactionData((prev) => ({
                ...prev,
                transactionType: option?.name || "",
              }));
            }}
            label="Transaction Type"
            color="bg-main-container-header"
            baseColor="bg-bg-page-body"
            borderColor="border-subtle"
            expandedBorderColor="border-button-primary"
          />

          <div className="flex flex-row gap-3 mt-4">
            <div className="flex-1">
              <LightCustomButton
                text="CANCEL"
                onClick={handleCloseEditSlider}
                buttonColor="bg-bg-page-body"
                borderColor="border-border-button-delete"
                isActive={true}
                className="w-full"
              />
            </div>
            <div className="flex-1">
              <LightCustomButton
                text="UPDATE"
                onClick={handleUpdateTransaction}
                isActive={
                  !transactionLoading &&
                  editTransactionData.refrenceNumber !== "" &&
                  editTransactionData.transactionType !== ""
                }
                isButtonLoading={transactionLoading}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </SlidingPanel>

      {/* Payment Details Popup */}
      <SlidingPanel
        isOpen={paymentDetailsOpen}
        onClose={() => setPaymentDetailsOpen(false)}
        heading="Payment Details"
        showHeader={true}
        isSlider={true}
        widthDesktop="500px">
        <div className="flex flex-col gap-[12px] p-4 mt-[64px]">
          {data.invoiceDetails?.transactionDetails && (
            <>
              <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
                <div className="flex flex-row justify-between">
                  <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
                    Payment
                  </div>
                  <DataPill
                    value={data.invoiceDetails.transactionDetails.status}
                  />
                </div>
                <LabelRow label="Transaction Type" value="Invoice Payment" />
                <LabelRow label="Source" value="Invoice" />
                <LabelRow
                  label="Created Date & Time"
                  value={constantFunctions.formatDateTime(
                    data.invoiceDetails.transactionDetails.createdAt,
                  )}
                />
                <LabelRow
                  label="Expires At"
                  value={constantFunctions.formatDateTime(
                    data.invoiceDetails.transactionDetails.expiresAt,
                  )}
                />
                <div className="border-b border-border-subtle"></div>
                <LabelRow
                  label="Order ID"
                  value={data.invoiceDetails.transactionDetails.orderId}
                  isCopy={true}
                  alignment="col"
                />
              </div>

              <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
                <div className="flex flex-row items-center gap-[4px]">
                  <div className="bg-bg-page-body rounded-full h-[24px] p-[2px] w-[24px] mr-[4px] flex items-center justify-center">
                    <img src={Info} alt="Info" />
                  </div>
                  <span className="text-[14px] font-normal text-text-base">
                    Payment Details
                  </span>
                </div>
                <LabelRow
                  label="Base Currency"
                  valueComp={
                    <div className="flex flex-row gap-[4px] items-center">
                      <img
                        src={data.baseCurrencyImage}
                        height={14}
                        width={14}
                        alt="currency icon"
                      />
                      <p className="text-text-base text-[12px] font-normal">
                        {data.baseCurrencySymbol}
                      </p>
                    </div>
                  }
                />
                <LabelRow
                  label="Settled Currency"
                  valueComp={
                    <div className="flex flex-row gap-[4px] items-center">
                      <img
                        src={
                          data.invoiceDetails.transactionDetails
                            .settledCurrencyImage
                        }
                        height={14}
                        width={14}
                        alt="currency icon"
                      />
                      <p className="text-text-base text-[12px] font-normal">
                        {data.invoiceDetails.transactionDetails.settledCurrency}
                      </p>
                    </div>
                  }
                />
                <LabelRow
                  label="Network"
                  value={data.invoiceDetails.transactionDetails.network}
                />
                <div className="border-b border-border-subtle"></div>
                <LabelRow
                  label="Requested in Base"
                  value={`${constantFunctions.formatAmount(
                    data.invoiceDetails.transactionDetails.baseAmount,
                  )} ${data.baseCurrencySymbol}`}
                />
                <LabelRow
                  label="Requested in Settled"
                  value={`${constantFunctions.formatAmount(
                    data.invoiceDetails.transactionDetails
                      .settledAmountRequested,
                  )} ${data.invoiceDetails.transactionDetails.settledCurrency}`}
                />
                <LabelRow
                  label="Received in Base"
                  value={`${constantFunctions.formatAmount(
                    data.invoiceDetails.transactionDetails.baseAmountReceived,
                  )} ${data.baseCurrencySymbol}`}
                />
                <LabelRow
                  label="Received in Settled"
                  value={`${constantFunctions.formatAmount(
                    data.invoiceDetails.transactionDetails
                      .settledAmountReceived,
                  )} ${data.invoiceDetails.transactionDetails.settledCurrency}`}
                />
                <LabelRow
                  label="Commissions"
                  value={`${constantFunctions.formatAmount(
                    data.invoiceDetails.transactionDetails.commission,
                  )} ${data.invoiceDetails.transactionDetails.settledCurrency}`}
                />
                <LabelRow
                  label="Credited"
                  value={`${constantFunctions.formatAmount(
                    data.invoiceDetails.transactionDetails
                      .settledAmountCredited,
                  )} ${data.invoiceDetails.transactionDetails.settledCurrency}`}
                />
                <LabelRow
                  label="Conversion Rate"
                  value={`1 ${data.baseCurrencySymbol} = ${data.invoiceDetails.transactionDetails.conversionRate} ${data.invoiceDetails.transactionDetails.settledCurrency}`}
                />
              </div>

              <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
                <div className="rounded-lg flex flex-col my-2">
                  <div className="flex flex-col rounded-lg p-[8px] bg-bg-trx-card-header rounded-t-lg rounded-b-none">
                    <span className="text-[12px] font-semibold text-center">
                      Transaction Details
                    </span>
                  </div>
                  <div className="flex flex-col bg-[#FFFFFF] p-[8px] rounded-t-none rounded-b-lg">
                    <div className="flex flex-row justify-between">
                      <span className="font-medium text-[12px] text-text-label">
                        To address
                      </span>
                      <CopyButton
                        copyText={
                          data.invoiceDetails.transactionDetails.depositAddress
                        }
                        copyMessage="Address copied to clipboard!"
                        fillColor=""
                      />
                    </div>
                    <span className="text-text-base font-medium text-[12px] break-all mt-1">
                      {data.invoiceDetails.transactionDetails.depositAddress ??
                        "- -"}
                    </span>
                    <div className="p-[8px] mt-[12px] flex flex-row justify-between w-full border-t border-t-border-subtle">
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-[12px] text-text-label">
                          Amount
                        </span>
                        <span className="font-medium text-[12px] text-text-base">
                          {constantFunctions.formatAmount(
                            data.invoiceDetails.transactionDetails
                              .settledAmountRequested,
                          )}{" "}
                          {
                            data.invoiceDetails.transactionDetails
                              .settledCurrency
                          }
                        </span>
                      </div>
                      <div className="w-px bg-border-subtle" />
                      <div className="flex flex-col">
                        <span className="font-medium text-[12px] text-text-label text-right">
                          Status
                        </span>
                        <DataPill
                          value={data.invoiceDetails.transactionDetails.status}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </SlidingPanel>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={documentViewerOpen}
        onClose={closeDocumentViewer}
        documentUrl={selectedDocumentUrl}
        documentTitle={selectedDocumentTitle}
      />
    </div>
  );
};

export default InvoiceApprovalsDetails;
